import React, {useState} from "react";
import {
  PlusIcon,
} from "@heroicons/react/24/outline";

// 05 Feb 2026
import {
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";

import {
  ComposedChart,
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

import moment from "moment";

import {
  useGetUserConversationMasterAllsQuery,
} from "@/features/userConversations/userConversationsApiSlice";

import { useGetOrganizationsQuery } from "@/features/organizations/organizationsApiSlice"; // 30 Jan 2026
import { useGetUsersQuery } from "@/features/users/usersApiSlice"; // 30 Jan 2026

import EtConversationContactNoteCard from "@/components/conversations/EtConversationContactNoteCard";

// 23 Jan 2026
import EtModalCreateUserConversationLanding from '@/components/common/modal/EtModalCreateUserConversationLanding';
import EtModalUpdateUserConversationLanding from '@/components/common/modal/EtModalUpdateUserConversationLanding';
import EtModalDeleteUserConversationLanding from '@/components/common/modal/EtModalDeleteUserConversationLanding';

// 26 Jan 2026
import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import User from "@/components/common/user/User"
import { userProfileIcon2 } from "@/assets/img/imgAssets"

// 29 Jan 2026
import useAuth from "@/hooks/useAuth";

// -----------------------------------------
// Utilities (reused)
// -----------------------------------------
const preprocessText = (text = "") =>
  text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

const tokenize = (text) =>
  text.toLowerCase().trim().split(/\s+/).filter(Boolean);

// 🎨 20 Jan 2026 - Stable user color palette (Compare mode)
const USER_COLOR_PALETTE = [
  "#EA580C", // orange-600
  "#2563EB", // blue-600
  "#16A34A", // green-600
  "#7C3AED", // violet-600
  "#DC2626", // red-600
  "#0D9488", // teal-600
  "#9333EA", // purple-600
  "#CA8A04", // yellow-600
];

// 20 Jan 2026 - Deterministic color resolver
const getUserColor = (userId, index = 0) => {
  if (!userId) return "#9CA3AF"; // gray-400 fallback
  return USER_COLOR_PALETTE[index % USER_COLOR_PALETTE.length];
};


// -----------------------------------------
// Component
// -----------------------------------------
const EtTrinityConversationCenter = ({ 
    userId = "",
    userOrgId = "",
    onClose 
}) => {

  // First Created - 16 Jan 2026
  // Author - Atul Pandey
  // Purpose - Let user manage all their conversations from one place. Canonical Conversation Center. Full reuse of Conversation Center architecture. Search + time filtering. Streak + Daily/Cumulative charts using Recharts.
  // Updated - 17 Jan 2026 - Added logic for separating self vs contact conversations and scopedConversations 
  // Updated - 18 Jan 2026 - Added logic for getting self (loggedInUser) vs. team / collective (all org users) data for display
  // Updated - 20 Jan 2026 - Refined the data for charts based on the new code
  // Updated - 23 Jan 2026 - Refined for adding a new conversation through Modal
  // Updated - 24 Jan 2026 - Refined for keeping log details private (for Collective) based on mode - full
  // Updated - 26 Jan 2026 - refined for contribution view in the collective 
  // Updated - 26 Jan 2026 - refined for purpose native universe view
  // Updated - 29 Jan 2026 - refined for EtAdmins to dig deeper by org in purpose native universe view
  // Updated - 30 Jan 2026 - continued refinements for EtAdmins to dig deeper by org in purpose native universe view
  // Updated - 30 Jan 2026 - get org data for org matching for etadmin view
  // Updated - 31 Jan 2026 - charts for byOrg
  // Updated - 01 Feb 2026 - added timeBucketDescriptions
  // Updated - 05 Feb 2026 - org name masking in byOrg
  // Updated - 06 Feb 2026 - added showOwner prop to EtReflectionCard
  // Updated - 20 Apr 2026 - Refined My Org view to also show charts
  // Updated - 29 Apr 2026 - Refined Collective view to show conversations by org, account, account contact map
  // Updated - 28 Jun 2026 - Refined Collective view to show conversations by contact, account, BU and org 
  // Updated - 29 Jun 2026 - Refined Collective for account and bu views

  // 0 - Debug for Incoming props data
  // console.log('Trinity Conversation Center, userId :', userId)
  // console.log('Trinity Conversation Center, userOrgId :', userOrgId)

  // 1a - 29 Jan 2026 - Determine user Auth state
  
  const { isEtAdmin, isEtCoach } = useAuth();
  const canViewAdminUniverse = isEtAdmin || isEtCoach;

  // -----------------------------------------
  // 1b. Local State
  // -----------------------------------------
  const [activeTimeBucket, setActiveTimeBucket] = React.useState("today");
  const [activeYear, setActiveYear] = React.useState(moment().year());
  const [searchText, setSearchText] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [activeViewTab, setActiveViewTab] = React.useState("log");
  const [chartTab, setChartTab] = React.useState("streak");

  // ⭐ Conversation Scope (All | Self | Contacts)
  const [conversationScope, setConversationScope] = React.useState("all");

  // 18 Jan 2026 - View Mode — Self vs Collective
  const [viewMode, setViewMode] = React.useState("self"); // "self" | "collective"
  const [activeCollectiveUserId, setActiveCollectiveUserId] = React.useState("all");

  // 🟠 20 Jan 2026 - Chart Mode — Aggregate vs Compare
  const [chartMode, setChartMode] = React.useState("aggregate"); // "aggregate" | "compare"

  // 23 Jan 2026 - states for create, update, delete conversation
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [showUpdateModal, setShowUpdateModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedDeleteConversation, setSelectedDeleteConversation] = useState(null);

  // ⭐ 29 Jan 2026 — Universe Sub Tabs (Admin only)
  const [universeTab, setUniverseTab] = React.useState("myOrg"); // myOrg | byOrg

  // ⭐ 31 Jan 2026 – selected user per org for chart focus
  const [selectedOrgUserMap, setSelectedOrgUserMap] = React.useState({});

  // ⭐ 05 Feb 2026 — Org name visibility (controls users too)
  const [showOrgNameMap, setShowOrgNameMap] = React.useState({});

  // ⭐ 29 Apr 2026 - Collective hierarchy selection state
  const [activeCollectiveLevel, setActiveCollectiveLevel] = React.useState("bu"); 
  // "bu" | "account" | "accountContactMap"

  const [selectedCollectiveBUId, setSelectedCollectiveBUId] = React.useState(null);
  const [selectedCollectiveAccountId, setSelectedCollectiveAccountId] = React.useState(null);
  const [selectedCollectiveAccountContactMapId, setSelectedCollectiveAccountContactMapId] = React.useState(null);

  // ⭐ 28 Jun 2026 - Simplified Collective Contributions
// Flat contribution levels instead of nested BU → Account → Contact Map navigation
const [activeContributionLevel, setActiveContributionLevel] = React.useState("contact");
// "contact" | "account" | "bu" | "org"

const [selectedContributionEntityId, setSelectedContributionEntityId] =
  React.useState(null);

  // -----------------------------------------
  // 2. Load Canonical Conversations
  // -----------------------------------------
  
  // ⭐ 26 Jan 2026 - Purpose Native Universe View
    const {
    data: conversationsResponse,
    isLoading,
    refetch: refetchUserConversationMaster,
    } = useGetUserConversationMasterAllsQuery();

    console.log('conversationsResponse', conversationsResponse)

  // ⭐ 26 Jan 2026 — All Conversations Raw
    const allConversationsRaw = React.useMemo(() => {
      if (!conversationsResponse) return [];
      if (Array.isArray(conversationsResponse)) return conversationsResponse;
      return conversationsResponse.conversations || [];
    }, [conversationsResponse]);

  // console.log('allConversationsRaw', allConversationsRaw)

  // ⭐ 26 Jan 2026 — Org Daily Totals Raw
    const orgDailyTotalsRaw = React.useMemo(() => {
      if (!conversationsResponse) return [];
      if (Array.isArray(conversationsResponse)) return [];
      return conversationsResponse.orgDailyTotals || [];
    }, [conversationsResponse]);

  // ⭐ 29 Jun 2026 - Commercial Account lookup for Collective Contributions
  const orgAccountsRaw = React.useMemo(() => {
    if (!conversationsResponse || Array.isArray(conversationsResponse)) {
      return [];
    }

    return conversationsResponse.orgAccounts || [];
  }, [conversationsResponse]);

  // ⭐ 26 Jan 2026 — Normalize Org Daily Totals by Date (Universe Baseline)
    const orgDailyTotalsByDate = React.useMemo(() => {
      if (!orgDailyTotalsRaw || orgDailyTotalsRaw.length === 0) return {};
  
      return orgDailyTotalsRaw.reduce((acc, item) => {
        // Defensive guards (backend already mostly clean, but be safe)
        if (!item?.date) return acc;
        if (typeof item.dailyCount !== "number") return acc;
  
        acc[item.date] = item.dailyCount;
        return acc;
      }, {});
    }, [orgDailyTotalsRaw]);

  // 30 Jan 2026 - Extract Organizations 
    const { data: organizationsData = [] } = useGetOrganizationsQuery();
    const { data: usersResponse } = useGetUsersQuery();
    // const { data: usersResponse } = useGetUsersQuery(undefined, {
    //   skip: !canViewAdminUniverse,
    // });
  
    const extractEntitiesFromAdapter = (dataAdapter) => {
      if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
      return dataAdapter.ids.map((id) => dataAdapter.entities[id]);
    };
  
    const organizations = extractEntitiesFromAdapter(organizationsData);
    const allUsers = extractEntitiesFromAdapter(usersResponse);
  
    // console.log('users', allUsers)
  
    // 30 Jan 2026 - Build OrgId to organizationName map to be used in byOrg tab in Universe view
    const orgIdToNameMap = React.useMemo(() => {
      const map = {};
      organizations.forEach(org => {
        map[org._id?.toString()] = org.organizationName || "Unknown Org";
      });
      return map;
    }, [organizations]);
  
    // console.log('orgIdToNameMap', orgIdToNameMap)
  
  // console.log("Org Daily Totals (Universe):", orgDailyTotalsByDate);

  // -----------------------------------------
  // 3a. Preprocess once (performance critical)
  // -----------------------------------------
  const allConversations = React.useMemo(() => {
    return allConversationsRaw.map(conv => ({
      ...conv,
      __plainText: preprocessText(
        conv.conversationSummary ||
        conv.conversationText ||
        ""
      ),
    }));
  }, [allConversationsRaw]);

  // console.log('all Conversations', allConversations)

// 3b. 18 Jan 2026 — Unique users from conversations (for Collective view)
const collectiveUsers = React.useMemo(() => {
    if (viewMode !== "collective") return [];

    const map = new Map();
    allConversations.forEach(c => {
        if (c.userId?._id && !map.has(c.userId._id)) {
        map.set(c.userId._id, {
            _id: c.userId._id,
            name: c.userId.name || c.userId.username || "User",
        });
        }
    });

    return Array.from(map.values());
}, [allConversations, viewMode]);

// 3c. Reset the users based on the viewMode
React.useEffect(() => {
  if (viewMode === "collective") {
    setActiveCollectiveUserId("all");
  }
}, [viewMode]);

// ⭐ 29 Apr 2026 - Reset collective hierarchy when leaving Collective view
React.useEffect(() => {
  if (viewMode !== "collective") {
    setActiveCollectiveLevel("bu");
    setSelectedCollectiveBUId(null);
    setSelectedCollectiveAccountId(null);
    setSelectedCollectiveAccountContactMapId(null);
  }
}, [viewMode]);

// ⭐ 28 Jun 2026 - Reset contribution selection outside Collective view
React.useEffect(() => {
  if (viewMode !== "collective") {
    setActiveContributionLevel("contact");
    setSelectedContributionEntityId(null);
  }
}, [viewMode]);

// ⭐ 25 Jan 2026 - Fix: prevent stale "compare" mode when collective user != Everyone
React.useEffect(() => {
  if (viewMode !== "collective") {
    setChartMode("aggregate");
    return;
  }

  if (activeCollectiveUserId !== "all") {
    setChartMode("aggregate");
  }
}, [viewMode, activeCollectiveUserId]);

// 29 Jan 2026 - Universe Subtab reset
  React.useEffect(() => {
    if (viewMode !== "universe") return;
    setUniverseTab("myOrg");
  }, [viewMode]);


// 3d. 18 Jan 2025 - Rehash Data Streams for Collective
// -----------------------------------------
// 1️⃣ View Mode Base (Self vs Collective)
// -----------------------------------------
const baseViewConversations = React.useMemo(() => {
  // Backend already scopes by role:
  // - Non OrgSponsor → self only
  // - OrgSponsor → org-wide
  return allConversations;
}, [allConversations]);

// -----------------------------------------
// 2️⃣ User Scope (Collective User Selector)
// -----------------------------------------
const userScopedConversations = React.useMemo(() => {
  // 20 Jan 2026
  // 🔐 SELF — always logged-in user
  if (viewMode === "self") {
    return baseViewConversations.filter(
      c => c.userId?._id === userId
    );
  }
  
  
//     // Self mode → no user-level filtering
//   if (viewMode !== "collective") {
//     return baseViewConversations;
//   }

  // Collective → Everyone
  if (activeCollectiveUserId === "all") {
    return baseViewConversations;
  }

  // Collective → Specific user
  return baseViewConversations.filter(
    c => c.userId?._id === activeCollectiveUserId
  );
}, [
    baseViewConversations, 
    viewMode, 
    activeCollectiveUserId, 
    userId,                     // 20 Jan 2026
]);

// -----------------------------------------
// 3️⃣ Conversation Type Scope (All | Self | Contacts)
// -----------------------------------------
const typeScopedConversations = React.useMemo(() => {
  switch (conversationScope) {
    case "self":
      return userScopedConversations.filter(
        c => !c.contactId && !c.contactName
      );

    case "contact":
      return userScopedConversations.filter(
        c => c.contactId?.contactName || c.contactName
      );

    case "all":
    default:
      return userScopedConversations;
  }
}, [userScopedConversations, conversationScope]);


  // -----------------------------------------
  // 4. Debounce Search
  // -----------------------------------------
  React.useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(h);
  }, [searchText]);

  // -----------------------------------------
  // 5. Time Boundaries (identical)
  // -----------------------------------------
  const now = moment();
  const startOfToday = moment().startOf("day");
  const endOfToday = moment().endOf("day");

  const startOfWeek = moment().isoWeekday(1).startOf("day");
  const endOfWeek = endOfToday;
  const actualEndOfWeek = moment().isoWeekday(7).endOf("day");

  const startOfMonth = moment().startOf("month");
  const endOfMonth = moment().isSame(now, "month")
    ? endOfToday
    : moment().endOf("month");

  const startOfQuarter = moment().startOf("quarter");
  const endOfQuarter = moment().isSame(now, "quarter")
    ? endOfToday
    : moment().endOf("quarter");

  const startOfYear = moment().startOf("year");
  const endOfYear = moment().isSame(now, "year")
    ? endOfToday
    : moment().endOf("year");

  // -----------------------------------------
  // 6. Time Filtering
  // -----------------------------------------
  const filterByTime = (items, start, end) =>
    items.filter(i =>
      moment(i.conversationDate).isBetween(start, end, null, "[]")
    );

  // 18 Jan 2026

  const conversationsToday = filterByTime(typeScopedConversations, startOfToday, endOfToday);
  const conversationsWeek = filterByTime(typeScopedConversations, startOfWeek, endOfWeek);
  const conversationsMonth = filterByTime(typeScopedConversations, startOfMonth, endOfMonth);
  const conversationsQuarter = filterByTime(typeScopedConversations, startOfQuarter, endOfQuarter);
  const conversationsYear = filterByTime(typeScopedConversations, startOfYear, endOfYear);



//   const conversationsToday = filterByTime(allConversations, startOfToday, endOfToday);
//   const conversationsWeek = filterByTime(allConversations, startOfWeek, endOfWeek);
//   const conversationsMonth = filterByTime(allConversations, startOfMonth, endOfMonth);
//   const conversationsQuarter = filterByTime(allConversations, startOfQuarter, endOfQuarter);
//   const conversationsYear = filterByTime(allConversations, startOfYear, endOfYear);

  // -----------------------------------------
  // 7. Determine Displayed Set
  // -----------------------------------------
  let displayedConversations = [];

  switch (activeTimeBucket) {
    case "week": displayedConversations = conversationsWeek; break;
    case "month": displayedConversations = conversationsMonth; break;
    case "quarter": displayedConversations = conversationsQuarter; break;
    case "year": displayedConversations = conversationsYear; break;

    case "byYear": {
      const start = moment().year(activeYear).startOf("year");
      const end = moment().year(activeYear).isSame(now, "year")
        ? endOfToday
        : moment().year(activeYear).endOf("year");
      // 18 Jan 2026
      displayedConversations = filterByTime(typeScopedConversations, start, end);
    //   displayedConversations = filterByTime(allConversations, start, end);
      break;
    }

    // 18 Jan 2026
    case "all": displayedConversations = typeScopedConversations; break;
    // case "all": displayedConversations = allConversations; break;
    case "today":
    default: displayedConversations = conversationsToday;
  }

  // -----------------------------------------
  // 8. Search Filtering
  // -----------------------------------------

  const searchFilteredConversations = React.useMemo(() => {
  if (!debouncedSearch.trim()) return displayedConversations;

  const searchTokens = tokenize(debouncedSearch);

  return displayedConversations.filter(c =>
    searchTokens.some(t => c.__plainText?.includes(t))
  );
}, [displayedConversations, debouncedSearch]);

// -----------------------------------------
// 9️⃣ Per-User Aggregation Map (FOUNDATION)
// -----------------------------------------
const conversationsByUser = React.useMemo(() => {
  const map = new Map();

  searchFilteredConversations.forEach(conv => {
    const userId = conv.userId?._id || "unknown";

    if (!map.has(userId)) {
      map.set(userId, {
        userId,
        userName:
          conv.userId?.name ||
          conv.userId?.username ||
          "Unknown User",
        conversations: [],
      });
    }

    map.get(userId).conversations.push(conv);
  });

  return map;
}, [searchFilteredConversations]);

// console.log('conversationsByUser: ',conversationsByUser);

// -----------------------------------------
// 10️⃣ Shared Time Bucket Generator
// -----------------------------------------
const buildDateBuckets = (start, end) => {
  const buckets = [];
  let cursor = start.clone();

  while (cursor.isSameOrBefore(end, "day")) {
    buckets.push(cursor.format("YYYY-MM-DD"));
    cursor.add(1, "day");
  }

  return buckets;
};

// 20 Jan 2026

// -----------------------------------------
// 9️⃣ Resolve Time Range (start / end)
// -----------------------------------------
const { startDate, endDate } = React.useMemo(() => {
  const items = searchFilteredConversations;
  if (!items.length) return {};

  let start, end;

  switch (activeTimeBucket) {
    case "week": start = startOfWeek; end = endOfWeek; break;
    case "month": start = startOfMonth; end = endOfMonth; break;
    case "quarter": start = startOfQuarter; end = endOfQuarter; break;

    case "year":
    case "byYear": {
      const targetYear =
        activeTimeBucket === "byYear" ? activeYear : moment().year();

      const yearItems = items.filter(c =>
        moment(c.conversationDate).year() === targetYear
      );

      if (yearItems.length) {
        const sorted = [...yearItems].sort((a, b) =>
          moment(a.conversationDate).diff(moment(b.conversationDate))
        );
        start = moment(sorted[0].conversationDate).startOf("day");
        end = moment(sorted.at(-1).conversationDate).endOf("day");
      } else {
        start = moment().year(targetYear).startOf("year");
        end = moment().year(targetYear).endOf("year");
      }
      break;
    }

    case "all": {
      const sorted = [...items].sort((a, b) =>
        moment(a.conversationDate).diff(moment(b.conversationDate))
      );
      start = moment(sorted[0].conversationDate).startOf("day");
      end = moment(sorted.at(-1).conversationDate).endOf("day");
      break;
    }

    default:
      start = startOfToday;
      end = endOfToday;
  }

  return { startDate: start, endDate: end };
}, [searchFilteredConversations, activeTimeBucket, activeYear]);

// -----------------------------------------
// 10️⃣ Bucket Dates (Shared)
// -----------------------------------------
const bucketDates = React.useMemo(() => {
  if (!startDate || !endDate) return [];
  return buildDateBuckets(startDate, endDate);
}, [startDate, endDate]);

// -----------------------------------------
// 11️⃣ Aggregate Time Buckets (ALL USERS)
// -----------------------------------------
const aggregateBuckets = React.useMemo(() => {
  if (!bucketDates.length) return [];

  const dateCountMap = {};
  bucketDates.forEach(d => (dateCountMap[d] = 0));

  searchFilteredConversations.forEach(conv => {
    const dateKey = moment(conv.conversationDate).format("YYYY-MM-DD");
    if (dateCountMap[dateKey] !== undefined) {
      dateCountMap[dateKey] += 1;
    }
  });

  let cumulative = 0;

  return bucketDates.map((d, index) => {
    const daily = dateCountMap[d];
    cumulative += daily;

    return {
      dateKey: d,
      xValue: index,
      xLabel: moment(d).format("DD MMM"),
      dailyCount: daily,
      cumulativeCount: cumulative,
    };
  });
}, [bucketDates, searchFilteredConversations]);

// -----------------------------------------
// 12️⃣ Per-User Time Buckets (COMPARE)
// -----------------------------------------

const perUserBuckets = React.useMemo(() => {
  if (!bucketDates.length) return [];

  const result = [];
  let colorIndex = 0;

  conversationsByUser.forEach(({ userId, userName, conversations }) => {
    const color = getUserColor(userId, colorIndex++);

    const dateCountMap = {};
    bucketDates.forEach(d => (dateCountMap[d] = 0));

    conversations.forEach(conv => {
      const dateKey = moment(conv.conversationDate).format("YYYY-MM-DD");
      if (dateCountMap[dateKey] !== undefined) {
        dateCountMap[dateKey] += 1;
      }
    });

    let cumulative = 0;

    const series = bucketDates.map((d, index) => {
      const daily = dateCountMap[d];
      cumulative += daily;

      return {
        userId,
        userName,
        color,                 // 🎨 attached here
        dateKey: d,
        xValue: index,
        xLabel: moment(d).format("DD MMM"),
        dailyCount: daily,
        cumulativeCount: cumulative,
      };
    });

    result.push({
      userId,
      userName,
      color,                  // 🎨 and here
      series,
    });
  });

  return result;
}, [bucketDates, conversationsByUser]);


// 20 Jan 2026

// -----------------------------------------
// 13️⃣ Aggregate Streak Data (Event-based)
// -----------------------------------------
const aggregateStreakData = React.useMemo(() => {
  if (!bucketDates.length) return [];

  const dateIndex = {};
  bucketDates.forEach((d, i) => {
    dateIndex[d] = i;
  });

  return searchFilteredConversations.map(conv => {
    const d = moment(conv.conversationDate);
    const dateKey = d.format("YYYY-MM-DD");

    return {
      conversationId: conv._id,
      dateKey,
      xValue: dateIndex[dateKey] ?? 0,
      xLabel: moment(dateKey).format("DD MMM"),
      epochMinutes: d.hours() * 60 + d.minutes(),
      createdAt: conv.conversationDate,
    };
  }).sort((a, b) => a.xValue - b.xValue);
}, [searchFilteredConversations, bucketDates]);


// -----------------------------------------
// 14️⃣ Per-User Streak Data (Compare-ready)
// -----------------------------------------


const perUserStreakData = React.useMemo(() => {
  if (!bucketDates.length) return [];

  const dateIndex = {};
  bucketDates.forEach((d, i) => {
    dateIndex[d] = i;
  });

  const result = [];
  let colorIndex = 0;

  conversationsByUser.forEach(({ userId, userName, conversations }) => {
    const color = getUserColor(userId, colorIndex++);

    const series = conversations.map(conv => {
      const d = moment(conv.conversationDate);
      const dateKey = d.format("YYYY-MM-DD");

      return {
        userId,
        userName,
        color, // 🎨 attached here
        conversationId: conv._id,
        dateKey,
        xValue: dateIndex[dateKey] ?? 0,
        xLabel: moment(dateKey).format("DD MMM"),
        epochMinutes: d.hours() * 60 + d.minutes(),
        createdAt: conv.conversationDate,
      };
    });

    result.push({
      userId,
      userName,
      color,        // 🎨 and here
      series: series.sort((a, b) => a.xValue - b.xValue),
    });
  });

  return result;
}, [conversationsByUser, bucketDates]);


// -----------------------------------------
// 10b. Daily & Cumulative Conversation Count
// (derived from aggregateBuckets)
// -----------------------------------------

const dailyCountData = React.useMemo(() => {
  if (!aggregateBuckets.length) return [];

  let cumulative = 0;

  return aggregateBuckets.map(bucket => {
    cumulative += bucket.dailyCount;

    return {
      xValue: bucket.xValue,
      dateKey: bucket.dateKey,
      xLabel: moment(bucket.dateKey).format("DD MMM"),
      dailyCount: bucket.dailyCount,
      cumulativeCount: cumulative,
    };
  });
}, [aggregateBuckets]);

// 20 Jan 2026
const activeChartSeries = React.useMemo(() => {
  if (chartMode === "aggregate") {
    return {
      mode: "aggregate",
      data: aggregateBuckets,
    };
  }

  return {
    mode: "compare",
    data: perUserBuckets,
  };
}, [chartMode, aggregateBuckets, perUserBuckets]);

const activeStreakData = React.useMemo(() => {
  if (chartMode === "compare") {
    return null; // will be replaced with per-user series
  }
  return aggregateStreakData;
}, [chartMode, aggregateStreakData]);

  // -----------------------------------------
  // 11 - Conversation Actions (wired later)
  // -----------------------------------------
  const handleEditConversation = (conversation) => {
    // 23 Jan 2026
    setSelectedConversation(conversation);
    // console.log('selectedConversation', selectedConversation)
    setShowUpdateModal(true);
  };

  const handleDeleteConversation = (conversation) => {
    // 23 Jan 2026
    setSelectedDeleteConversation(conversation);
    setShowDeleteModal(true);
  };

  // ⭐ 12a — derive available years for "By Year" selector (Conversation Center)
  const availableYears = React.useMemo(() => {
    const years = new Set();

    allConversations.forEach(conv => {
        if (conv.conversationDate) {
        years.add(moment(conv.conversationDate).year());
        }
    });

    const sorted = Array.from(years).sort((a, b) => b - a);

    // Always include current year
    const currentYear = moment().year();
    if (!sorted.includes(currentYear)) {
        sorted.unshift(currentYear);
    }

    return sorted;
  }, [allConversations]);

  // -----------------------------------------
  // 12b. Helper for screening if user can see others' conversations - 24 Jan 2026
  // -----------------------------------------
  const canViewFullConversation = (conversation, loggedInUserId) => {
    // Always allow own conversations
    if (conversation.userId?._id === loggedInUserId) return true;

    // Allow if explicitly shared
    if (Array.isArray(conversation.sharedWith)) {
      return conversation.sharedWith.some(
        sw => sw.userId === loggedInUserId || sw.userId?._id === loggedInUserId
      );
    }

    return false;
  };

  // ⭐ 26 Jan 2026 - Time-scoped conversations (used by charts + contributions)
  const timeScopedConversations = React.useMemo(() => {
      switch (activeTimeBucket) {
        case "week":
          return conversationsWeek;
        case "month":
          return conversationsMonth;
        case "quarter":
          return conversationsQuarter;
        case "year":
          return conversationsYear;
        case "byYear":
          return userScopedConversations.filter(c =>
            moment(c.conversationDate).year() === activeYear
          );
        case "all":
          return userScopedConversations;
        case "today":
        default:
          return conversationsToday;
      }
    }, [
      activeTimeBucket,
      conversationsToday,
      conversationsWeek,
      conversationsMonth,
      conversationsQuarter,
      conversationsYear,
      userScopedConversations,
      activeYear,
  ]);
  
  // ⭐ 26 Jan 2026 - Group time-scoped conversations by user
  const timeScopedConversationsByUser = React.useMemo(() => {
    const map = new Map();
  
    timeScopedConversations.forEach(c => {
      const uid = c.userId?._id;
      if (!uid) return;
  
      if (!map.has(uid)) {
        map.set(uid, {
          userId: uid,
          userName: c.userId?.name ||  "User",
          username: c.userId?.username || "",
          conversations: [],
        });
      }
  
      map.get(uid).conversations.push(c);
    });
  
    return Array.from(map.values());
  }, [timeScopedConversations]);


  // -----------------------------------------
  // ⭐ 29 Apr 2026 - Collective Hierarchy Helpers
  // BU → Account → Account Contact Map
  // -----------------------------------------

  const getEntityIdOld = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (value?._id) return value._id.toString();
    return value.toString?.() || null;
  };

  const getEntityNameOld = (value, fallback = "Unassigned") => {
    if (!value) return fallback;

    return (
      value.organizationBUName ||
      value.buName ||
      value.accountName ||
      value.accountContactMapName ||
      value.contactName ||
      value.name ||
      value.title ||
      fallback
    );
  };

  // ⭐ 29 Apr 2026 - Resolve BU from conversation
  const getConversationBUOld = (conv) => {
    const bu =
      conv.currentOrgBUId ||
      conv.organizationBUId ||
      conv.orgBUId ||
      conv.userOrgBUMapId?.organizationBUId ||
      conv.currentUserOrgBUMapId?.organizationBUId;

    const id = getEntityId(bu);
    const name = getEntityName(bu, id ? "Business Unit" : "Organization");

    return {
      id: id || "org-level",
      name: id ? name : "Organization Level",
      type: id ? "bu" : "org",
    };
  };

  // ⭐ 29 Apr 2026 - Resolve Account from conversation
  const getConversationAccountOld = (conv) => {
    const account =
      conv.currentAccountId ||
      conv.accountId ||
      conv.currentUserAccountMapId?.accountId ||
      conv.userAccountMapId?.accountId;

    const id = getEntityId(account);
    const name = getEntityName(account, id ? "Account" : "BU Level");

    return {
      id: id || "bu-level",
      name: id ? name : "BU Level",
      type: id ? "account" : "bu",
    };
  };

  // ⭐ 29 Apr 2026 - Resolve Account Contact Map from conversation
  const getConversationAccountContactMap = (conv) => {
    const accountContactMap =
      conv.currentAccountContactMapId ||
      conv.accountContactMapId ||
      conv.currentUserAccountContactMapId ||
      conv.userAccountContactMapId ||
      conv.currentAccountContactMap ||
      conv.accountContactMap;

    const id = getEntityId(accountContactMap);

    const name =
      getEntityName(accountContactMap, null) ||
      conv.contactId?.contactName ||
      conv.contactName ||
      "Contact Map Level";

    return {
      id: id || "account-level",
      name: id ? name : "Account Level",
      type: id ? "accountContactMap" : "account",
    };
  };

  // ⭐ 29 Apr 2026 - Build user contribution snapshot for any conversation subset
  const buildContributionSnapshot = (items = []) => {
    const userMap = new Map();

    items.forEach((conv) => {
      const uid = conv.userId?._id;
      if (!uid) return;

      if (!userMap.has(uid)) {
        userMap.set(uid, {
          userId: uid,
          userName: conv.userId?.name || "User",
          username: conv.userId?.username || "",
          conversations: [],
        });
      }

      userMap.get(uid).conversations.push(conv);
    });

    const contributorsRaw = Array.from(userMap.values());
    const total = contributorsRaw.reduce((sum, u) => sum + u.conversations.length, 0);

    if (!total) {
      return {
        total: 0,
        contributors: [],
      };
    }

    return {
      total,
      contributors: contributorsRaw
        .map((u) => ({
          userId: u.userId,
          userName: u.userName,
          username: u.username,
          count: u.conversations.length,
          percentOfTotal: Math.round((u.conversations.length / total) * 100),
        }))
        .sort((a, b) => b.count - a.count),
    };
  };

  // ⭐ 29 Apr 2026 - Build cumulative trend for any conversation subset
  const buildCumulativeSeries = (items = []) => {
    if (!bucketDates.length) return [];

    const dateCountMap = {};
    bucketDates.forEach((d) => {
      dateCountMap[d] = 0;
    });

    items.forEach((conv) => {
      const dateKey = moment(conv.conversationDate).format("YYYY-MM-DD");
      if (dateCountMap[dateKey] !== undefined) {
        dateCountMap[dateKey] += 1;
      }
    });

    let cumulative = 0;

    return bucketDates.map((dateKey, index) => {
      const dailyCount = dateCountMap[dateKey] || 0;
      cumulative += dailyCount;

      return {
        xValue: index,
        dateKey,
        xLabel: moment(dateKey).format("DD MMM"),
        dailyCount,
        cumulativeCount: cumulative,
      };
    });
  };

  // -----------------------------------------
  // ⭐ 28 Jun 2026 - Simplified Collective Contribution Helpers
  // Flat levels: Contact | Account | BU | Org
  // -----------------------------------------

  const getEntityId = (value) => {
    if (!value) return null;

    if (typeof value === "string") {
      return value;
    }

    if (value?._id) {
      return value._id.toString();
    }

    return value.toString?.() || null;
  };

  const getEntityName = (value, fallback = "Unassigned") => {
    if (!value) return fallback;

    // console.log('value', value)

    return (
      value.contactName ||
      value.accountName ||
      value.organizationBUName ||
      value.organizationName ||
      value.name ||
      fallback
    );
  };

  // ⭐ Important:
  // Conversation records may hold these fields directly,
  // or they may be available through populated userId.
  const getConversationUser = (conv) => {
    return conv?.userId || {};
  };

  // -----------------------------------------
  // Resolve Contact
  // -----------------------------------------
  const getConversationContact = (conv) => {
    const contact =
      conv.contactId ||
      conv.contact ||
      conv.contactOrganizationContactId;

    const id = getEntityId(contact);

    const name =
      getEntityName(contact, null) ||
      conv.contactName ||
      "Unnamed Contact";

    return {
      id,
      name,
    };
  };

  // ⭐ 29 Jun 2026 - Lookup Account by Contact Organization
  const accountByContactOrganizationId = React.useMemo(() => {
    const map = new Map();

    orgAccountsRaw.forEach((account) => {
      const contactOrganizationId = getEntityId(
        account.contactOrganizationId
      );

      if (!contactOrganizationId) {
        return;
      }

      map.set(contactOrganizationId, {
        id: getEntityId(account),
        name: account.accountName || "Unnamed Account",
        account,
      });
    });

    return map;
  }, [orgAccountsRaw]);

  // ⭐ 29 Jun 2026 - Lookup BUs by Account
  const busByAccountId = React.useMemo(() => {
    const map = new Map();

    orgAccountsRaw.forEach((account) => {
      const accountId = getEntityId(account);

      if (!accountId) {
        return;
      }

      const buNodes = (account.availableToOrganizationBUs || [])
        .map((item) => {
          const bu = item.organizationBUId;
          const buId = getEntityId(bu);

          if (!buId) {
            return null;
          }

          return {
            id: buId,
            name: bu.organizationBUName || "Unnamed BU",
          };
        })
        .filter(Boolean);

      map.set(accountId, buNodes);
    });

    return map;
  }, [orgAccountsRaw]);

  // ⭐ 29 Jun 2026
  // Resolve the commercial Account from the conversation's
  // contact organization, not from the contributor's current assignment.
  const getConversationAccount = (conv) => {
    const contactOrgId = getEntityId(
      conv.contactId?.contactOrgId ||
      conv.contactOrgId
    );

    if (!contactOrgId) {
      return null;
    }

    return accountByContactOrganizationId.get(contactOrgId) || null;
  };

  
  // ⭐ 29 Jun 2026
  // An Account may be available to multiple BUs.
  // Therefore this returns an array, not a single BU.
  const getConversationBUs = (conv) => {
    const account = getConversationAccount(conv);

    if (!account?.id) {
      return [];
    }

    return busByAccountId.get(account.id) || [];
  };

  // -----------------------------------------
  // Build flat contribution nodes for one level
  // -----------------------------------------
  const buildContributionNodes = (items = [], resolveEntity) => {
    const entityMap = new Map();

    items.forEach((conv) => {
      const entity = resolveEntity(conv);

      // Contact, Account, and BU levels should not create
      // misleading "Unassigned" aggregate cards.
      if (!entity?.id) {
        return;
      }

      if (!entityMap.has(entity.id)) {
        entityMap.set(entity.id, {
          id: entity.id,
          name: entity.name,
          conversations: [],
        });
      }

      entityMap.get(entity.id).conversations.push(conv);
    });

    return Array.from(entityMap.values())
      .map((entity) => ({
        ...entity,
        totalConversations: entity.conversations.length,
        contributionSnapshot: buildContributionSnapshot(entity.conversations),
        trendSeries: buildCumulativeSeries(entity.conversations),
      }))
      .sort((a, b) => b.totalConversations - a.totalConversations);
  };

  // ⭐ 29 Jun 2026 - A conversation can belong to multiple BUs
  // when its Account is available to multiple Organization BUs.
  const buildBUContributionNodes = (items = []) => {
    const buMap = new Map();

    items.forEach((conv) => {
      const bus = getConversationBUs(conv);

      bus.forEach((bu) => {
        if (!bu?.id) {
          return;
        }

        if (!buMap.has(bu.id)) {
          buMap.set(bu.id, {
            id: bu.id,
            name: bu.name,
            conversations: [],
          });
        }

        buMap.get(bu.id).conversations.push(conv);
      });
    });

    return Array.from(buMap.values())
      .map((bu) => ({
        ...bu,
        totalConversations: bu.conversations.length,
        contributionSnapshot: buildContributionSnapshot(bu.conversations),
        trendSeries: buildCumulativeSeries(bu.conversations),
      }))
      .sort((a, b) => b.totalConversations - a.totalConversations);
  };

  // -----------------------------------------
  // ⭐ 28 Jun 2026 - Flat contribution datasets
  // -----------------------------------------
  const collectiveContributionData = React.useMemo(() => {
    const source = timeScopedConversations || [];

    const contactNodes = buildContributionNodes(
      source,
      getConversationContact
    );

    // ⭐ 29 Jun 2026 All conversations that are tied to a specific Contact.
    // Excludes self / team / general conversations with no contact.
    const allContactConversations = source.filter((conv) => {
      const contact = getConversationContact(conv);
      return Boolean(contact?.id);
    });

    const allContactsNode = {
      id: "all-contacts",
      name: "All Contacts",
      conversations: allContactConversations,
      totalConversations: allContactConversations.length,
      contributionSnapshot: buildContributionSnapshot(
        allContactConversations
      ),
      trendSeries: buildCumulativeSeries(allContactConversations),
    };

    const accountNodes = buildContributionNodes(
      source,
      getConversationAccount
    );

    // ⭐ 29 Jun 2026 - All conversations that resolve to a commercial Account.
    // Excludes self / team conversations and contact conversations whose
    // Contact Organization is not yet linked to an Account.
    const allAccountConversations = source.filter((conv) => {
      const account = getConversationAccount(conv);
      return Boolean(account?.id);
    });

    const allAccountsNode = {
      id: "all-accounts",
      name: "All Accounts",
      conversations: allAccountConversations,
      totalConversations: allAccountConversations.length,
      contributionSnapshot: buildContributionSnapshot(
        allAccountConversations
      ),
      trendSeries: buildCumulativeSeries(allAccountConversations),
    };

    // const contactNodes = buildContributionNodes(
    //   source,
    //   getConversationContact
    // );

    // const accountNodes = buildContributionNodes(
    //   source,
    //   getConversationAccount
    // );

    // 29 Jun 2026
    const buNodes = buildBUContributionNodes(source);

    // ⭐ 29 Jun 2026 - All conversations that resolve to at least one Business Unit.
    // A conversation is included once here even when its Account is
    // available to multiple BUs.
    const allBUConversations = source.filter((conv) => {
      const bus = getConversationBUs(conv);
      return Array.isArray(bus) && bus.length > 0;
    });

    const allBUsNode = {
      id: "all-bus",
      name: "All BUs",
      conversations: allBUConversations,
      totalConversations: allBUConversations.length,
      contributionSnapshot: buildContributionSnapshot(
        allBUConversations
      ),
      trendSeries: buildCumulativeSeries(allBUConversations),
    };

    // const buNodes = buildContributionNodes(
    //   source,
    //   getConversationBU
    // );

    const orgNode = {
      id: "org-level",
      name: "Organization Level",
      conversations: source,
      totalConversations: source.length,
      contributionSnapshot: buildContributionSnapshot(source),
      trendSeries: buildCumulativeSeries(source),
    };

    return {
      allContactsNode, // 29 Jun 2026
      contactNodes,
      allAccountsNode, // 29 Jun 2026
      accountNodes,
      allBUsNode,      // 29 Jun 2026
      buNodes,
      orgNode,
    };
  }, [timeScopedConversations, bucketDates]);

  // -----------------------------------------
  // Available top tabs
  // Only show Account / BU when applicable data exists.
  // -----------------------------------------
  const availableContributionLevels = React.useMemo(() => {
    const levels = [
      {
        value: "contact",
        label: "By Contact",
        available: collectiveContributionData.contactNodes.length > 0,
      },
      {
        value: "account",
        label: "By Account",
        available: collectiveContributionData.accountNodes.length > 0,
      },
      {
        value: "bu",
        label: "By BU",
        available: collectiveContributionData.buNodes.length > 0,
      },
      {
        value: "org",
        label: "Org Level",
        available: true,
      },
    ];

    return levels.filter((level) => level.available);
  }, [collectiveContributionData]);

  // -----------------------------------------
  // Selected entity list based on active tab
  // -----------------------------------------
  const activeContributionEntities = React.useMemo(() => {
    switch (activeContributionLevel) {

      // 29 Jun 2026
      case "account":
        return [
          collectiveContributionData.allAccountsNode,
          ...collectiveContributionData.accountNodes,
        ].filter((entity) => entity?.totalConversations > 0);
      
      // case "account":
      //   return collectiveContributionData.accountNodes;

      // 29 Jun 2026
      case "bu":
        return [
          collectiveContributionData.allBUsNode,
          ...collectiveContributionData.buNodes,
        ].filter((entity) => entity?.totalConversations > 0);

      // case "bu":
      //   return collectiveContributionData.buNodes;

      // 29 Jun 2026
      case "contact":
        return [
          collectiveContributionData.allContactsNode,
          ...collectiveContributionData.contactNodes,
        ].filter((entity) => entity?.totalConversations > 0);

      // case "contact":
      //   return collectiveContributionData.contactNodes;

      case "org":
      default:
        return [collectiveContributionData.orgNode];
    }
  }, [activeContributionLevel, collectiveContributionData]);

  // -----------------------------------------
  // Selected node for chart + contribution cards
  // -----------------------------------------
  const activeContributionNode = React.useMemo(() => {
    if (!activeContributionEntities.length) {
      return null;
    }

    return (
      activeContributionEntities.find(
        (entity) => entity.id === selectedContributionEntityId
      ) || activeContributionEntities[0]
    );
  }, [activeContributionEntities, selectedContributionEntityId]);

  // -----------------------------------------
  // Keep active contribution level valid
  // Example: Account tab disappears after time filter changes.
  // -----------------------------------------
  React.useEffect(() => {
    const isCurrentLevelAvailable = availableContributionLevels.some(
      (level) => level.value === activeContributionLevel
    );

    if (!isCurrentLevelAvailable) {
      setActiveContributionLevel(
        availableContributionLevels[0]?.value || "org"
      );
    }
  }, [availableContributionLevels, activeContributionLevel]);

  // -----------------------------------------
  // Reset entity selection when changing level
  // -----------------------------------------
  React.useEffect(() => {
    setSelectedContributionEntityId(null);
  }, [activeContributionLevel]);


  // -----------------------------------------
  // ⭐ 29 Apr 2026 - Collective Hierarchy Analytics
  // BU → Account → Account Contact Map
  // -----------------------------------------

  // const collectiveHierarchyData = React.useMemo(() => {
  //   const source = timeScopedConversations || [];

  //   const buMap = new Map();

  //   source.forEach((conv) => {
  //     const bu = getConversationBU(conv);
  //     const account = getConversationAccount(conv);
  //     const accountContactMap = getConversationAccountContactMap(conv);

  //     if (!buMap.has(bu.id)) {
  //       buMap.set(bu.id, {
  //         id: bu.id,
  //         name: bu.name,
  //         type: bu.type,
  //         conversations: [],
  //         accounts: new Map(),
  //       });
  //     }

  //     const buNode = buMap.get(bu.id);
  //     buNode.conversations.push(conv);

  //     if (!buNode.accounts.has(account.id)) {
  //       buNode.accounts.set(account.id, {
  //         id: account.id,
  //         name: account.name,
  //         type: account.type,
  //         conversations: [],
  //         accountContactMaps: new Map(),
  //       });
  //     }

  //     const accountNode = buNode.accounts.get(account.id);
  //     accountNode.conversations.push(conv);

  //     if (!accountNode.accountContactMaps.has(accountContactMap.id)) {
  //       accountNode.accountContactMaps.set(accountContactMap.id, {
  //         id: accountContactMap.id,
  //         name: accountContactMap.name,
  //         type: accountContactMap.type,
  //         conversations: [],
  //       });
  //     }

  //     const contactMapNode = accountNode.accountContactMaps.get(accountContactMap.id);
  //     contactMapNode.conversations.push(conv);
  //   });

  //   return Array.from(buMap.values())
  //     .map((bu) => ({
  //       ...bu,
  //       contributionSnapshot: buildContributionSnapshot(bu.conversations),
  //       trendSeries: buildCumulativeSeries(bu.conversations),
  //       accounts: Array.from(bu.accounts.values())
  //         .map((account) => ({
  //           ...account,
  //           contributionSnapshot: buildContributionSnapshot(account.conversations),
  //           trendSeries: buildCumulativeSeries(account.conversations),
  //           accountContactMaps: Array.from(account.accountContactMaps.values())
  //             .map((contactMap) => ({
  //               ...contactMap,
  //               contributionSnapshot: buildContributionSnapshot(contactMap.conversations),
  //               trendSeries: buildCumulativeSeries(contactMap.conversations),
  //             }))
  //             .sort((a, b) => b.conversations.length - a.conversations.length),
  //         }))
  //         .sort((a, b) => b.conversations.length - a.conversations.length),
  //     }))
  //     .sort((a, b) => b.conversations.length - a.conversations.length);
  // }, [timeScopedConversations, bucketDates]);

  // ⭐ 29 Apr 2026 - Selected BU node
  // const selectedCollectiveBU = React.useMemo(() => {
  //   if (!collectiveHierarchyData.length) return null;

  //   return (
  //     collectiveHierarchyData.find((bu) => bu.id === selectedCollectiveBUId) ||
  //     collectiveHierarchyData[0]
  //   );
  // }, [collectiveHierarchyData, selectedCollectiveBUId]);

  // ⭐ 29 Apr 2026 - Selected Account node
  // const selectedCollectiveAccount = React.useMemo(() => {
  //   if (!selectedCollectiveBU?.accounts?.length) return null;

  //   return (
  //     selectedCollectiveBU.accounts.find((account) => account.id === selectedCollectiveAccountId) ||
  //     selectedCollectiveBU.accounts[0]
  //   );
  // }, [selectedCollectiveBU, selectedCollectiveAccountId]);

  // ⭐ 29 Apr 2026 - Selected Account Contact Map node
  // const selectedCollectiveAccountContactMap = React.useMemo(() => {
  //   if (!selectedCollectiveAccount?.accountContactMaps?.length) return null;

  //   return (
  //     selectedCollectiveAccount.accountContactMaps.find(
  //       (contactMap) => contactMap.id === selectedCollectiveAccountContactMapId
  //     ) || selectedCollectiveAccount.accountContactMaps[0]
  //   );
  // }, [selectedCollectiveAccount, selectedCollectiveAccountContactMapId]);

  // ⭐ 29 Apr 2026 - Active hierarchy node for right-side display
  // const activeCollectiveHierarchyNode = React.useMemo(() => {
  //   if (activeCollectiveLevel === "accountContactMap") {
  //     return selectedCollectiveAccountContactMap;
  //   }

  //   if (activeCollectiveLevel === "account") {
  //     return selectedCollectiveAccount;
  //   }

  //   return selectedCollectiveBU;
  // }, [
  //   activeCollectiveLevel,
  //   selectedCollectiveBU,
  //   selectedCollectiveAccount,
  //   selectedCollectiveAccountContactMap,
  // ]);


  // -----------------------------------------
  // ⭐ 26 Jan 2026 - Collective Conversation Contribution Snapshot
  // -----------------------------------------
  
  const conversationContributionSnapshot = React.useMemo(() => {
    if (!timeScopedConversationsByUser.length) return null;
  
    const total = timeScopedConversationsByUser.reduce(
      (sum, u) => sum + u.conversations.length,
      0
    );
  
    if (total === 0) return null;
  
    return {
      total,
      contributors: timeScopedConversationsByUser.map(u => ({
        userId: u.userId,
        userName: u.userName,
        username: u.username,
        count: u.conversations.length,
        percentOfTotal: Math.round((u.conversations.length / total) * 100),
      }))
      .sort((a, b) => b.percentOfTotal - a.percentOfTotal),
    };
  }, [timeScopedConversationsByUser]);
  
  // 26 Jan 2026 - Handle Collective User Select - Switch from Everyone to other users for "Contributions" Tab
  const handleCollectiveUserSelect = (userId) => {
    setActiveCollectiveUserId(userId);
  
    if (userId !== "all" && activeViewTab === "contributions") {
      setActiveViewTab("log");
    }
  };

// -----------PURPOSE NATIVE UNIVERSE BLOCK -------------------


 // ⭐ 26 Jan 2026 - For filterig for Universe views
  const isUniverseView = viewMode === "universe";

  // ⭐ 26 Jan 2026 — Universe: normalize org daily totals by date
  const universeDateCountMap = React.useMemo(() => {
    const map = {};

    orgDailyTotalsRaw.forEach(row => {
      if (!row?.date || typeof row.dailyCount !== "number") return;

      // date already comes as YYYY-MM-DD from backend
      map[row.date] = (map[row.date] || 0) + row.dailyCount;
    });

    return map;
  }, [orgDailyTotalsRaw]);

  // ⭐ 26 Jan 2026 — Universe bucket dates (time scoped)
  const universeBucketDates = React.useMemo(() => {
    if (!isUniverseView) return [];

    let start, end;

    switch (activeTimeBucket) {
      case "week":
        start = startOfWeek;
        end = endOfWeek;
        break;
      case "month":
        start = startOfMonth;
        end = endOfMonth;
        break;
      case "quarter":
        start = startOfQuarter;
        end = endOfQuarter;
        break;
      case "year":
        start = startOfYear;
        end = endOfYear;
        break;
      case "byYear": {
        start = moment().year(activeYear).startOf("year");
        end = moment().year(activeYear).isSame(moment(), "year")
          ? endOfToday
          : moment().year(activeYear).endOf("year");
        break;
      }
      case "all": {
        const dates = Object.keys(universeDateCountMap);
        if (!dates.length) return [];

        const sorted = dates.sort();
        start = moment(sorted[0]);
        end = moment(sorted[sorted.length - 1]);
        break;
      }
      case "today":
      default:
        start = startOfToday;
        end = endOfToday;
        break;
    }

    const buckets = [];
    let cursor = start.clone();

    while (cursor.isSameOrBefore(end, "day")) {
      buckets.push(cursor.format("YYYY-MM-DD"));
      cursor.add(1, "day");
    }

    return buckets;
  }, [
    isUniverseView,
    activeTimeBucket,
    universeDateCountMap,
    startOfToday,
    endOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
    activeYear,
  ]);

  // ⭐ 26 Jan 2026 — Universe totals for active time bucket
  const universeTimeScopedTotals = React.useMemo(() => {
    if (!orgDailyTotalsRaw.length) return [];

    return orgDailyTotalsRaw.filter(d => {
      if (!d.date) return false;
      const m = moment(d.date);

      switch (activeTimeBucket) {
        case "today":
          return m.isSame(startOfToday, "day");

        case "week":
          return m.isBetween(startOfWeek, endOfWeek, null, "[]");

        case "month":
          return m.isBetween(startOfMonth, endOfMonth, null, "[]");

        case "quarter":
          return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");

        case "year":
          return m.isBetween(startOfYear, endOfYear, null, "[]");

        case "byYear":
          return m.year() === activeYear;

        case "all":
        default:
          return true;
      }
    });
  }, [
    orgDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 26 Jan 2026 - Universe aggregate count (no users involved)
  const universeTotalCount = React.useMemo(() => {
    return universeTimeScopedTotals.reduce(
      (sum, d) => sum + (d.dailyCount || 0),
      0
    );
  }, [universeTimeScopedTotals]);

  // ⭐ 26 Jan 2026 — Universe daily + cumulative series (chart-ready)
  const universeDailyCumulativeData = React.useMemo(() => {
    if (!isUniverseView || !universeBucketDates.length) return [];

    let cumulative = 0;

    return universeBucketDates.map((dateKey, index) => {
      const dailyCount = universeDateCountMap[dateKey] || 0;
      cumulative += dailyCount;

      return {
        xValue: index,
        dateKey,
        xLabel: moment(dateKey).format("DD MMM"),
        dailyCount,
        cumulativeCount: cumulative,
      };
    });
  }, [
    isUniverseView,
    universeBucketDates,
    universeDateCountMap,
  ]);

  // ⭐ 26 Jan 2026 — My contribution within Universe scope
  const myUniverseContributionCount = React.useMemo(() => {
    if (!allConversationsRaw.length) return 0;

    return allConversationsRaw.filter(r => {
      if (!r.conversationDate) return false;

      if (r.userOrgId?.toString() !== userOrgId?.toString()) return false;


      const m = moment(r.conversationDate);

      switch (activeTimeBucket) {
        case "today":
          return m.isSame(startOfToday, "day");

        case "week":
          return m.isBetween(startOfWeek, endOfWeek, null, "[]");

        case "month":
          return m.isBetween(startOfMonth, endOfMonth, null, "[]");

        case "quarter":
          return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");

        case "year":
          return m.isBetween(startOfYear, endOfYear, null, "[]");

        case "byYear":
          return m.year() === activeYear;

        case "all":
        default:
          return true;
      }
    }).length;
  }, [
    allConversationsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 26 Jan 2026 — My share of universe (%)
  const myUniverseSharePct = React.useMemo(() => {
    if (!universeTotalCount) return 0;
    return Math.round((myUniverseContributionCount / universeTotalCount) * 100);
  }, [myUniverseContributionCount, universeTotalCount]);

  // ⭐ 26 Jan 2026 — Universe daily intensity
  const universeDailyAverage = React.useMemo(() => {
    if (!universeBucketDates.length) return 0;
    return Math.round(universeTotalCount / universeBucketDates.length);
  }, [universeTotalCount, universeBucketDates]);

  // ⭐ 26 Jan 2026 — Universe totals per org (for ranking)
  const universeTotalsByOrg = React.useMemo(() => {
  const map = {};

    universeTimeScopedTotals.forEach(row => {
      if (!row?.orgId || typeof row.dailyCount !== "number") return;

      // const orgId = row.orgId.toString();

      const orgId =
      row.orgId?._id?.toString?.() ??
      row.orgId?.toString?.() ??
      null;

      if (!orgId) return;

      map[orgId] = (map[orgId] || 0) + row.dailyCount;
    });

    return map;
  }, [universeTimeScopedTotals]);

  // ⭐ 26 Jan 2026 — Universe org totals (sorted)
  const universeOrgTotalsSorted = React.useMemo(() => {
    return Object.entries(universeTotalsByOrg)
      .map(([orgId, total]) => ({ orgId, total }))
      .sort((a, b) => b.total - a.total);
  }, [universeTotalsByOrg]);

  // ⭐ 26 Jan 2026 — My org rank in universe
  const universeRankMeta = React.useMemo(() => {
    if (!userOrgId || !universeOrgTotalsSorted.length) return null;

    const myOrgId = userOrgId.toString();

    const index = universeOrgTotalsSorted.findIndex(
      o => o.orgId === myOrgId
    );

    if (index === -1) {
      return {
        myRank: null,
        totalOrgs: universeOrgTotalsSorted.length,
        myOrgTotal: 0,
      };
    }

    return {
      myRank: index + 1,
      totalOrgs: universeOrgTotalsSorted.length,
      myOrgTotal: universeOrgTotalsSorted[index].total,
    };
  }, [universeOrgTotalsSorted, userOrgId]);

  // ⭐ 26 Jan 2026 — Universe benchmarks
  const universeBenchmarks = React.useMemo(() => {
    if (!universeOrgTotalsSorted.length) return null;

    const totals = universeOrgTotalsSorted.map(o => o.total);
    const max = totals[0];
    const min = totals[totals.length - 1];
    const median =
      totals.length % 2 === 1
        ? totals[Math.floor(totals.length / 2)]
        : Math.round(
            (totals[totals.length / 2 - 1] + totals[totals.length / 2]) / 2
          );

    return { max, min, median };
  }, [universeOrgTotalsSorted]);

  // ⭐ 26 Jan 2026 — Human-friendly Universe time label
  const universeTimeLabel = React.useMemo(() => {
    switch (activeTimeBucket) {
      case "today":
        return "today";
      case "week":
        return "this week";
      case "month":
        return "this month";
      case "quarter":
        return "this quarter";
      case "year":
        return "this year";
      case "byYear":
        return `year ${activeYear}`;
      case "all":
        return "all time periods";
      default:
        return activeTimeBucket;
    }
  }, [activeTimeBucket, activeYear]);

  // ⭐⭐ 29 Jan 2026 - orgUserDailyTotalsRaw
  const orgUserDailyTotalsRaw = React.useMemo(() => {
    if (!conversationsResponse || Array.isArray(conversationsResponse)) return [];
    return conversationsResponse.orgUserDailyTotals || [];
  }, [conversationsResponse]);

  // ⭐⭐ 30 Jan 2026
  const universeByOrgMap = React.useMemo(() => {
    const map = {};

    const isInActiveTimeBucket = (dateStr) => {
      const m = moment(dateStr);

      switch (activeTimeBucket) {
        case "today":
          return m.isSame(startOfToday, "day");

        case "week":
          return m.isBetween(startOfWeek, endOfWeek, null, "[]");

        case "month":
          return m.isBetween(startOfMonth, endOfMonth, null, "[]");

        case "quarter":
          return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");

        case "year":
          return m.isBetween(startOfYear, endOfYear, null, "[]");

        case "byYear":
          return m.year() === activeYear;

        case "all":
        default:
          return true;
      }
    };

    orgUserDailyTotalsRaw
      .filter(row => row?.date && isInActiveTimeBucket(row.date))
      .forEach(row => {
        const orgId = row.orgId?.toString();
        const userId = row.userId?.toString();

        if (!orgId || !userId) return;

        if (!map[orgId]) {
          map[orgId] = {
            total: 0,
            users: {},
          };
        }

        map[orgId].total += row.dailyCount;
        map[orgId].users[userId] =
          (map[orgId].users[userId] || 0) + row.dailyCount;
      });

    return map;
  }, [
    orgUserDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐⭐ 30 Jan 2026   
  const userIdToFirstNameMap = React.useMemo(() => {
    const map = {};

    allUsers.forEach(user => {
      if (!user?._id) return;

      const displayName =
        user.name ||
        user.username ||
        "User";

      map[user._id.toString()] = displayName.split(" ")[0];
    });

    return map;
  }, [allUsers]);

  // ⭐ 31 Jan 2026 
  const universeOrgCumulativeSeries = React.useMemo(() => {
    const map = {};

    orgDailyTotalsRaw
      .filter(row => {
        if (!row?.date) return false;
        const m = moment(row.date);

        switch (activeTimeBucket) {
          case "today":
            return m.isSame(startOfToday, "day");
          case "week":
            return m.isBetween(startOfWeek, endOfWeek, null, "[]");
          case "month":
            return m.isBetween(startOfMonth, endOfMonth, null, "[]");
          case "quarter":
            return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");
          case "year":
            return m.isBetween(startOfYear, endOfYear, null, "[]");
          case "byYear":
            return m.year() === activeYear;
          case "all":
          default:
            return true;
        }
      })
      .forEach(row => {
        const orgId = row.orgId?.toString();
        if (!orgId) return;

        if (!map[orgId]) {
          map[orgId] = {};
        }

        map[orgId][row.date] =
          (map[orgId][row.date] || 0) + row.dailyCount;
      });

    // convert to cumulative series
    const result = {};

    Object.entries(map).forEach(([orgId, dateMap]) => {
      let cumulative = 0;

      const series = Object.keys(dateMap)
        .sort()
        .map((date, idx) => {
          cumulative += dateMap[date];
          return {
            xValue: idx,
            dateKey: date,
            xLabel: moment(date).format("DD MMM"),
            cumulativeCount: cumulative,
          };
        });

      result[orgId] = series;
    });

    return result;
  }, [
    orgDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 31 Jan 2026 - rankBgClass - color the org ranks
  const rankBgClass = (idx) =>
  idx === 0
    ? "bg-lime-600 text-white"
    : idx === 1
    ? "bg-yellow-400 text-gray-900"
    : idx === 2
    ? "bg-orange-200 text-gray-900"
    : idx === 3
    ? "bg-purple-100 text-gray-900"
    : idx === 4
    ? "bg-blue-100 text-gray-900"
    : "bg-gray-100 text-gray-700";

  // ⭐ 31 Jan 2026 - get selected users for an org for charting
  const getSelectedUserForOrg = (orgId, users) =>
    selectedOrgUserMap[orgId] || users?.[0]?.[0];

  const handleUserSelect = (orgId, userId) => {
    setSelectedOrgUserMap(prev => ({
      ...prev,
      [orgId]: userId,
    }));
  };

  // ⭐ 31 Jan 2026 – Org → User cumulative reflection series
  const universeUserCumulativeSeries = React.useMemo(() => {
    const map = {};

    orgUserDailyTotalsRaw
      .filter(row => {
        if (!row?.date) return false;
        const m = moment(row.date);

        switch (activeTimeBucket) {
          case "today":
            return m.isSame(startOfToday, "day");
          case "week":
            return m.isBetween(startOfWeek, endOfWeek, null, "[]");
          case "month":
            return m.isBetween(startOfMonth, endOfMonth, null, "[]");
          case "quarter":
            return m.isBetween(startOfQuarter, endOfQuarter, null, "[]");
          case "year":
            return m.isBetween(startOfYear, endOfYear, null, "[]");
          case "byYear":
            return m.year() === activeYear;
          case "all":
          default:
            return true;
        }
      })
      .forEach(row => {
        const orgId = row.orgId?.toString();
        const userId = row.userId?.toString();
        if (!orgId || !userId) return;

        if (!map[orgId]) map[orgId] = {};
        if (!map[orgId][userId]) map[orgId][userId] = {};

        map[orgId][userId][row.date] =
          (map[orgId][userId][row.date] || 0) + row.dailyCount;
      });

    const result = {};

    Object.entries(map).forEach(([orgId, userMap]) => {
      result[orgId] = {};

      Object.entries(userMap).forEach(([userId, dateMap]) => {
        let cumulative = 0;

        result[orgId][userId] = Object.keys(dateMap)
          .sort()
          .map((date, idx) => {
            cumulative += dateMap[date];
            return {
              xValue: idx,
              dateKey: date,
              xLabel: moment(date).format("DD MMM"),
              cumulativeCount: cumulative,
            };
          });
      });
    });

    return result;
  }, [
    orgUserDailyTotalsRaw,
    activeTimeBucket,
    activeYear,
    startOfToday,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
  ]);

  // ⭐ 20 Apr 2026 - My Org universe snapshot
  const myOrgUniverseData = React.useMemo(() => {
    return universeByOrgMap[userOrgId?.toString()] || { total: 0, users: {} };
  }, [universeByOrgMap, userOrgId]);

  // ⭐ 20 Apr 2026 - ranked users for My Org block
  const myOrgRankedUsers = React.useMemo(() => {
    return Object.entries(myOrgUniverseData?.users || {}).sort((a, b) => b[1] - a[1]);
  }, [myOrgUniverseData]);

  // ⭐ 20 Apr 2026 - My Org cumulative trend
  const myOrgUniverseSeries = React.useMemo(() => {
    return universeOrgCumulativeSeries[userOrgId?.toString()] || [];
  }, [universeOrgCumulativeSeries, userOrgId]);

  // ⭐ 20 Apr 2026 - Universe compare lines by org for My Org tab
  const universeOrgCompareSeries = React.useMemo(() => {
    const myOrgId = userOrgId?.toString();

    return universeOrgTotalsSorted.map((org, idx) => {
      const isMyOrg = org.orgId === myOrgId;

      return {
        orgId: org.orgId,
        label: isMyOrg ? "My Org" : `Org ${idx + 1}`,
        total: org.total,
        isMyOrg,
        series: universeOrgCumulativeSeries[org.orgId] || [],
      };
    });
  }, [universeOrgTotalsSorted, universeOrgCumulativeSeries, userOrgId]);

  // ⭐ 20 Apr 2026 - selected contributor inside My Org block
  const selectedMyOrgUserId = React.useMemo(() => {
    return getSelectedUserForOrg(userOrgId?.toString(), myOrgRankedUsers);
  }, [userOrgId, myOrgRankedUsers, selectedOrgUserMap]);

  // ⭐ 20 Apr 2026 - Sorting data for user based charting
  const myOrgUserSeriesMap = React.useMemo(() => {
    return universeUserCumulativeSeries?.[userOrgId?.toString()] || {};
  }, [universeUserCumulativeSeries, userOrgId]);

  const selectedMyOrgUserIdForChart = React.useMemo(() => {
    if (
      selectedMyOrgUserId &&
      Array.isArray(myOrgUserSeriesMap[selectedMyOrgUserId]) &&
      myOrgUserSeriesMap[selectedMyOrgUserId].length > 0
    ) {
      return selectedMyOrgUserId;
    }

    const fallback = myOrgRankedUsers.find(
      ([uid]) =>
        Array.isArray(myOrgUserSeriesMap[uid]) &&
        myOrgUserSeriesMap[uid].length > 0
    );

    return fallback?.[0] || null;
  }, [selectedMyOrgUserId, myOrgRankedUsers, myOrgUserSeriesMap]);

  const selectedMyOrgUserSeries = React.useMemo(() => {
    if (!selectedMyOrgUserIdForChart) return [];
    return myOrgUserSeriesMap[selectedMyOrgUserIdForChart] || [];
  }, [myOrgUserSeriesMap, selectedMyOrgUserIdForChart]);


  // --------------------------------------------------
  // 13. Time Scope Explainer (Conversations) - 01 Feb 2026
  // --------------------------------------------------
  const timeBucketDescription = React.useMemo(() => {
    switch (activeTimeBucket) {
      case "today":
        return (
          <>
            Today is{" "}
            <span className="font-semibold">
              {now.format("DD MMM YYYY")}
            </span>
          </>
        );

      case "week":
        return (
          <>
            This week — Mon (
            <span className="font-semibold">
              {startOfWeek.format("DD MMM YYYY")}
            </span>
            ) - Sun (
            <span className="font-semibold">
              {actualEndOfWeek.format("DD MMM YYYY")}
            </span>
            )
          </>
        );

      case "month":
        return (
          <>
            This month is{" "}
            <span className="font-semibold">
              {startOfMonth.format("MMMM YYYY")}
            </span>
          </>
        );

      case "quarter": {
        const quarter = now.quarter();
        const endOfQuarterActual = moment()
          .year(now.year())
          .quarter(quarter)
          .endOf("quarter");

        return (
          <>
            This quarter is{" "}
            <span className="font-semibold text-orange-600">
              Q{quarter} of {now.year()}
            </span>
            . Starting:{" "}
            <span className="font-semibold">
              {startOfQuarter.format("DD MMM YYYY")}
            </span>
            , Ending:{" "}
            <span className="font-semibold">
              {endOfQuarterActual.format("DD MMM YYYY")}
            </span>
          </>
        );
      }

      case "year":
        return (
          <>
            This year is{" "}
            <span className="font-semibold">
              {now.year()}
            </span>
          </>
        );

      case "byYear":
        return (
          <>
            Showing data for year{" "}
            <span className="font-semibold">
              {activeYear}
            </span>
          </>
        );

      case "all":
        return (
          <>
            <span className="font-semibold">All</span> the logged creations
          </>
        );

      default:
        return null;
    }
  }, [
    activeTimeBucket,
    activeYear,
    now,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    startOfQuarter,
  ]);

  // 🔐 05 Feb 2026 — Cryptic Org Name helper
  const getCrypticOrgName = (name = "") => {
    if (!name) return "ORG";

    const parts = name.split(" ").filter(Boolean);
    if (parts.length > 1) {
      return parts.map(p => p[0]).join("").toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // 🔐 05 Feb 2026 — Cryptic User Name helper
  const getCrypticUserName = (fullName = "") => {
    if (!fullName) return "U";

    const parts = fullName.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0][0].toUpperCase();
  };

  // 05 Feb 2026
  const toggleOrgNameVisibility = (orgId) => {
    setShowOrgNameMap(prev => ({
      ...prev,
      [orgId]: !prev[orgId],
    }));
  };


  // -----------------------------------------
  // 14. Render
  // -----------------------------------------
  return (
    <div className="p-6 bg-white shadow rounded max-w-6xl mx-auto mt-4">

        {/* Header */}
        <div className="flex justify-between mb-4">
            <h2 className="text-xl font-bold">Conversation Center</h2>
            <button onClick={onClose} className="text-sm text-blue-600">Close</button>
        </div>

        {/* Count */}
        <div className="flex justify-center items-center mb-4 bg-orange-100 py-4 rounded">
            <span className="text-lg mr-2">Conversations:</span>
            <div className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
            {/* {finalConversations.length} */}
            {/* {scopedConversations.length} */} {/* 18 Jan 2026 - commented */}
            {/* 18 Jan 2026 - added*/}
            {searchFilteredConversations.length}
            </div>
        </div>

        <div className="flex justify-between items-center w-full">

            {/* Time Filters */}
            <div className="flex space-x-2 mb-4 w-full">
                {[
                ["Today", "today"],
                ["Week", "week"],
                ["Month", "month"],
                ["Quarter", "quarter"],
                ["Year", "year"],
                ["By Year", "byYear"],
                ["All", "all"],
                ].map(([label, value]) => (
                <button
                    key={value}
                    onClick={() => setActiveTimeBucket(value)}
                    className={`px-3 py-1 rounded-full text-sm border ${
                    activeTimeBucket === value
                        ? "bg-orange-600 text-white"
                        : "bg-gray-100"
                    }`}
                >
                    {label}
                </button>
                ))}
            </div>

            {/* 18 Jan 2026 - View Mode Toggle — Self / Collective */}
            <div className="flex space-x-2 mb-4 p-2 bg-gray-50 rounded">
                <button
                    onClick={() => setViewMode("self")}
                    className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    viewMode === "self"
                        ? "bg-orange-600 text-white border-orange-700"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                >
                    Self
                </button>

                <button
                    onClick={() => setViewMode("collective")}
                    className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    viewMode === "collective"
                        ? "bg-orange-600 text-white border-orange-700"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                >
                    Collective
                </button>

                {/* 26 Jan 2026 - Universe view */}
                <button
                  onClick={() => setViewMode("universe")}
                  className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    viewMode === "universe"
                      ? "bg-orange-600 text-white border-orange-700"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                  }`}
                >
                  Universe
                </button>

            </div>

        </div>
      
        {/* 01 Feb 2026 - Time Scope Explainers */}
        {timeBucketDescription && (
          <div className="mx-1 mt-1 mb-3 px-3 py-2 bg-orange-100 border-l-4 border-orange-500 rounded">
            <p className="text-sm text-gray-700">
              {timeBucketDescription}
            </p>
            
          </div>
        )}

        {/* ⭐ By Year Selector (Conversation Center) */}
        {activeTimeBucket === "byYear" && (
            <div className="flex space-x-2 mb-4 animate-slide-in-left">
                {availableYears.map((year) => (
                <button
                    key={year}
                    onClick={() => setActiveYear(year)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border shadow ${
                    activeYear === year
                        ? "bg-orange-600 text-white border-orange-800"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                >
                    {year}
                </button>
                ))}
            </div>
        )}

        {/* Search */}
        {/* <input
            type="text"
            placeholder="Search conversations…"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="w-full border px-3 py-2 rounded mb-4"
        /> */}

      
        {/* --------------------------------------------- */}
        {/* CONTEXT HEADER — View Tabs + Scope + Users */}
        {/* --------------------------------------------- */}
        <div className="mb-4">

        {/* 🔹 Collective User Selector (only when Collective) */}
        {viewMode === "collective" && (
            <div className="flex items-center space-x-2 overflow-x-auto pb-3">
            <button
                onClick={() => setActiveCollectiveUserId("all")}
                className={`px-3 py-1 rounded-full text-sm font-semibold border whitespace-nowrap ${
                activeCollectiveUserId === "all"
                    ? "bg-orange-600 text-white border-orange-700"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
            >
                Everyone
            </button>

            {collectiveUsers.map(user => (
                <button
                key={user._id}
                onClick={() => handleCollectiveUserSelect(user._id)} // 26 Jan 2026
                // onClick={() => setActiveCollectiveUserId(user._id)}
                className={`px-3 py-1 rounded-full text-sm font-semibold border whitespace-nowrap ${
                    activeCollectiveUserId === user._id
                    ? "bg-orange-600 text-white border-orange-700"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
                >
                {user.name?.split(" ")[0]}
                </button>
            ))}
            </div>
        )}

        {/* 🔹 View Tabs + Conversation Scope */}
        {viewMode !== "universe" && (
        <div className="flex justify-between items-center bg-gray-50 rounded w-full p-2">

            {/* View Tabs */}
            <div className="flex space-x-2">
              <button
                  onClick={() => setActiveViewTab("log")}
                  className={`text-sm font-semibold px-3 py-1 rounded-t ${
                  activeViewTab === "log"
                      ? "bg-orange-200 text-orange-800"
                      : "text-gray-500"
                  }`}
              >
                  Log View
              </button>

              <button
                  onClick={() => setActiveViewTab("chart")}
                  className={`text-sm font-semibold px-3 py-1 rounded-t ${
                  activeViewTab === "chart"
                      ? "bg-orange-200 text-orange-800"
                      : "text-gray-500"
                  }`}
              >
                  Chart View
              </button>

              {/* ⭐ 26 Jan 2026 — Contributions View */}
              {viewMode === "collective" && activeCollectiveUserId === "all" && (
                <button
                  onClick={() => setActiveViewTab("contributions")}
                  className={`text-sm font-semibold px-3 py-1 rounded-t 
                    ${activeViewTab === "contributions" ? "bg-orange-200 text-orange-800" : "text-gray-500"}`}
                >
                  Contributions
                </button>
              )}
            </div>

            {/* Conversation Scope */}
            <div className="flex space-x-2">
            {[
                { label: "All", value: "all" },
                { label: "Self", value: "self" },
                { label: "Contacts", value: "contact" },
            ].map(({ label, value }) => (
                <button
                key={value}
                onClick={() => setConversationScope(value)}
                className={`px-3 py-1 rounded-full text-sm font-semibold border shadow ${
                    conversationScope === value
                    ? "bg-orange-600 text-white border-orange-700"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
                >
                {label}
                </button>
            ))}
            </div>
        </div>)}

        </div>

        {/* --------------------------------------------- */}
        {/* LOG VIEW — SHARED */}
        {/* --------------------------------------------- */}
        {viewMode !== "universe" && activeViewTab === "log" && (
        <div className="space-y-3">

            {/* 23 Jan 2026 - Add Button */}
            <div className="flex justify-start items-center my-3">
              <p className="font-bold text-xs mx-2">Add a conversation</p>
              <button type="button" onClick={() => setShowCreateModal(true)}>
                <PlusIcon className="h-5 w-5 text-white p-1 rounded bg-lime-600 hover:bg-green-600" />
              </button>
            </div>

            {isLoading ? (
            <p className="text-sm text-gray-500">Loading conversations…</p>
            ) : searchFilteredConversations.length === 0 ? (
            <p className="text-sm text-orange-500 p-2">
                No conversations logged yet for {activeTimeBucket}.
            </p>
            ) : (

              <>
                {/* 23 Jan 2026 - Shifted Search inside Log */}
                <input
                    type="text"
                    placeholder="Search conversations…"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    className="w-full border px-3 py-2 rounded mb-4"
                />
                
                {searchFilteredConversations.map((conv, idx) => {

                  // 24 Jan 2026 
                  
                  const allowFullView = canViewFullConversation(conv, userId);

                  const cardMode =
                  viewMode === "collective" && !allowFullView
                    ? "summary"
                    : "full";
                  
                  return (
                    <EtConversationContactNoteCard
                    key={conv._id}
                    note={conv}
                    index={idx}
                    onEdit={handleEditConversation}
                    onDelete={handleDeleteConversation}
                    showOwner={viewMode === "collective" && activeCollectiveUserId === "all"} // ✅ 23 Jan 2026
                    mode={cardMode} // 24 Jan 2026
                    />
                )})}
                
                </>


            )}
        </div>
        )}

        {/* --------------------------------------------- */}
        {/* CHART VIEW — SHARED */}
        {/* --------------------------------------------- */}
        {viewMode !== "universe" && activeViewTab === "chart" && (
        <>
            {searchFilteredConversations.length === 0 && (
            <p className="text-sm text-orange-500 px-4">
                No conversations available for this scope and time range.
            </p>
            )}

            <div className="my-8 py-6 bg-white rounded shadow-sm border border-gray-200">
            {/* ⭐ Chart Sub Tabs */}
            <div className="flex space-x-2 mb-4 px-4">
                <button
                onClick={() => setChartTab("streak")}
                className={`text-sm font-semibold px-3 py-1 rounded ${
                    chartTab === "streak"
                    ? "bg-orange-200 text-orange-800"
                    : "text-gray-500"
                }`}
                >
                Streak View
                </button>

                <button
                onClick={() => setChartTab("counts")}
                className={`text-sm font-semibold px-3 py-1 rounded ${
                    chartTab === "counts"
                    ? "bg-orange-200 text-orange-800"
                    : "text-gray-500"
                }`}
                >
                Daily & Cumulative
                </button>
            </div>

            {/* 🟠 20 Jan 2026 - Aggregate vs Compare Toggle */}
            {viewMode === "collective" && activeCollectiveUserId === "all" && (
                <div className="flex space-x-2 mb-4 px-4">
                <button
                    onClick={() => setChartMode("aggregate")}
                    className={`px-2 py-0.5 text-xs rounded-full font-semibold border ${
                    chartMode === "aggregate"
                        ? "bg-orange-600 text-white border-orange-700"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    }`}
                >
                    Aggregate
                </button>

                <button
                    onClick={() => setChartMode("compare")}
                    disabled={viewMode !== "collective"}
                    className={`px-2 py-0.5 text-xs rounded-full font-semibold border ${
                    chartMode === "compare"
                        ? "bg-orange-600 text-white border-orange-700"
                        : "bg-gray-100 text-gray-700 border-gray-300"
                    } ${viewMode !== "collective" ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                    Compare
                </button>
                </div>
            )}


                {/* ⬇️ Existing chart JSX */}

                {/* ------------------------------------------------ */}
                {/* STREAK VIEW — Time of Day vs Date */}
                {/* ------------------------------------------------ */}
                {chartTab === "streak" && (
                <>
                    <p className="text-md font-semibold text-gray-700 mb-3 px-4">
                    Conversation Streak — Time of Day vs Date
                    </p>

                    <div className="w-full h-[450px] text-xs">
                    <ResponsiveContainer>
                        <LineChart
                        // data={chartData}
                        // data={aggregateStreakData}
                        data={chartMode === "aggregate" ? aggregateStreakData : []}
                        margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                        >
                        <CartesianGrid strokeDasharray="3 3" />

                        {/* X AXIS — Dates */}
                        <XAxis
                            type="number"
                            dataKey="xValue"
                            domain={["dataMin", "dataMax"]}
                            // ticks={chartData.map(d => d.xValue)}
                            // ticks={aggregateStreakData.map(d => d.xValue)}
                            // tickFormatter={(x) =>
                            // // chartData.find(d => d.xValue === x)?.xLabel || ""
                            // aggregateStreakData.find(d => d.xValue === x)?.xLabel || ""
                            // }

                            // 20 Jan 2026
                            ticks={bucketDates.map((_, i) => i)}
                            tickFormatter={(x) =>
                                bucketDates[x]
                                    ? moment(bucketDates[x]).format("DD MMM")
                                    : ""
                            }
                        />

                        {/* Y AXIS — Time of Day */}
                        <YAxis
                            type="number"
                            domain={[60, 1440]}
                            ticks={[60, 180, 360, 540, 720, 900, 1080, 1260]}
                            tickFormatter={(m) =>
                            moment().startOf("day").add(m, "minutes").format("h A")
                            }
                            label={{
                            value: "Time of Day",
                            angle: -90,
                            position: "insideLeft",
                            offset: -3,
                            }}
                        />

                        {/* TOOLTIP */}
                        <Tooltip
                            content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const p = payload[0]?.payload;

                            const dateStr = moment(p.createdAt).format(
                                "ddd, DD MMM YYYY"
                            );
                            const timeStr = moment(p.createdAt).format("h:mm A");

                            return (
                                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                <p className="font-semibold text-gray-700">{dateStr}</p>
                                <p className="text-sm text-gray-600">{timeStr}</p>
                                </div>
                            );
                            }}
                        />

                        <Legend wrapperStyle={{ paddingTop: "20px" }} />

                        {/* LINE */}
                        {/* 20 Jan 2026 */}
                        {chartMode === "aggregate" && (
                            <Line
                            type="monotone"
                            dataKey="epochMinutes"
                            stroke="#EA580C"
                            strokeWidth={2}
                            dot={{ r: 4, fill: "#EA580C" }}
                            name="Conversation"
                            connectNulls={true}
                            />
                        )}

                        {/* 20 Jan 2026 */}
                        {chartMode === "compare" &&
                            perUserStreakData.map((userSeries) => (
                            <Line
                                key={userSeries.userId}
                                data={userSeries.series}
                                type="monotone"
                                dataKey="epochMinutes"
                                // stroke="#EA580C"
                                stroke={userSeries.color}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                name={userSeries.userName}
                                connectNulls={true}
                            />
                         ))}

                         {/* Before 20 Jan 2026 */}
                        {/* <Line
                            type="monotone"
                            dataKey="epochMinutes"
                            stroke="#EA580C"
                            strokeWidth={2}
                            dot={{ r: 4, fill: "#EA580C" }}
                            name="Conversation"
                            connectNulls={true}
                        /> */}
                        </LineChart>
                    </ResponsiveContainer>
                    </div>
                </>
                )}

                {/* ------------------------------------------------ */}
                {/* DAILY + CUMULATIVE VIEW */}
                {/* ------------------------------------------------ */}
                {chartTab === "counts" && (
                <div className="w-full h-[450px] px-4 text-xs">
                    <p className="text-md font-semibold text-gray-700 mb-3">
                    Daily Conversations & Cumulative Trend
                    </p>

                    <ResponsiveContainer>
                    <LineChart
                        // data={dailyCountData}
                        // 20 Jan 2026
                        data={chartMode === "aggregate" ? dailyCountData : []}
                        margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />

                        {/* X AXIS */}
                        <XAxis
                        type="number"
                        dataKey="xValue"
                        ticks={dailyCountData.map(d => d.xValue)}
                        tickFormatter={(x) =>
                            dailyCountData.find(d => d.xValue === x)?.xLabel || ""
                        }
                        />

                        {/* Y AXIS */}
                        <YAxis
                        type="number"
                        allowDecimals={false}
                        label={{
                            value: "Daily & Cumulative Count",
                            angle: -90,
                            position: "insideLeft",
                            dx: -10,
                            dy: 0,
                            style: { textAnchor: "middle" },
                        }}
                        />

                        {/* TOOLTIP */}
                        <Tooltip
                        content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const p = payload[0]?.payload;

                            const dateStr = moment(p.dateKey).format(
                            "ddd, DD MMM YYYY"
                            );

                            return (
                            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                <p className="font-semibold text-gray-700">{dateStr}</p>
                                <p className="text-sm text-gray-600">
                                Daily: {p.dailyCount}
                                </p>
                                <p className="text-sm text-gray-600">
                                Cumulative: {p.cumulativeCount}
                                </p>
                            </div>
                            );
                        }}
                        />

                        <Legend wrapperStyle={{ paddingTop: "20px" }} />

                        {/* 20 Jan 2026 */}
                       
                       {/* 🟠 AGGREGATE MODE (unchanged) */}
                        {(chartMode === "aggregate" ||
                            activeCollectiveUserId !== "all") && (
                            <>

                                {/* DAILY BAR */}
                                <Bar
                                    dataKey="dailyCount"
                                    fill="#65a30d"
                                    name="Daily Conversations"
                                    barSize={24}
                                />

                                {/* CUMULATIVE LINE */}
                                <Line
                                    type="monotone"
                                    dataKey="cumulativeCount"
                                    stroke="#EA580C"
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: "#EA580C" }}
                                    name="Cumulative Conversations"
                                />
                            </>
                        )}

                        {/* 🔵 COMPARE MODE — PER USER */}

                        {chartMode === "compare" &&
                        perUserBuckets.map(userSeries => (
                            <Line
                            key={userSeries.userId}
                            // key={`daily-${userSeries.userId}`}
                            data={userSeries.series}
                            type="monotone"
                            dataKey="cumulativeCount"
                            stroke={userSeries?.color}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            name={userSeries.userName}
                            connectNulls
                            />
                        ))}
                        {/* {chartMode === "compare" &&
                            perUserBuckets.map((user) => (
                            <Line
                                key={user.userId}
                                data={user.series}
                                type="monotone"
                                dataKey="cumulativeCount"
                                stroke="#94a3b8"
                                strokeWidth={1.5}
                                dot={false}
                                name={user.userName}
                                connectNulls
                            />
                            ))} */}

                        {/* 🟠 COMPARE MODE — AGGREGATE OVERLAY */}
                        {chartMode === "compare" && (
                            <Line
                            data={dailyCountData}
                            type="monotone"
                            dataKey="cumulativeCount"
                            stroke="#EA580C"
                            strokeWidth={3}
                            dot={false}
                            name="Everyone (Total)"
                            />
                        )}
                        

                        
                        {/* Before - 20 Jan 2026 */}
                        {/* <Bar
                            dataKey="dailyCount"
                            fill="#65a30d"
                            name="Daily Conversations"
                            barSize={24}
                        />

                        <Line
                            type="monotone"
                            dataKey="cumulativeCount"
                            stroke="#EA580C"
                            strokeWidth={2}
                            dot={{ r: 3, fill: "#EA580C" }}
                            name="Cumulative Conversations"
                        /> */}
                    </LineChart>
                    </ResponsiveContainer>
                </div>
                )}

            </div>
        </>
        )}

        {/* --------------------------------------------- */}
        {/* ⭐ 28 Jun 2026 - SIMPLIFIED CONTRIBUTIONS VIEW */}
        {/* Contact | Account | BU | Org */}
        {/* --------------------------------------------- */}
        {viewMode === "collective" &&
          activeViewTab === "contributions" &&
          activeCollectiveUserId === "all" && (
            <div className="mt-6 animate-slide-in-left">
              <div className="bg-white rounded-lg border shadow-sm p-4">

                {/* Heading */}
                <div className="mb-4">
                  <p className="text-lg font-bold">
                    Collective Contributions
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    See collective conversation momentum and each user&apos;s
                    contribution within a selected contact, account, business unit,
                    or the organization overall.
                  </p>
                </div>

                {/* Top Contribution Level Tabs */}
                <div className="flex flex-wrap gap-2 border-b pb-3 mb-4">
                  {availableContributionLevels.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setActiveContributionLevel(level.value)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
                        activeContributionLevel === level.value
                          ? "bg-orange-600 text-white border-orange-700"
                          : "bg-gray-50 text-gray-600 border-gray-300 hover:bg-orange-50"
                      }`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>

                {/* Selected Contact / Account / BU chips */}
                {activeContributionLevel !== "org" &&
                  activeContributionEntities.length > 0 && (
                    <div className="mb-5">
                      <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-2">
                        Select{" "}
                        {activeContributionLevel === "contact"
                           ? activeContributionNode?.id === "all-contacts"
                            ? "All Contacts"
                            : "Contact"
                          : activeContributionLevel === "account"
                          ? activeContributionNode?.id === "all-accounts"
                            ? "All Accounts"
                            : "Account"
                          : activeContributionLevel === "bu"
                          ? activeContributionNode?.id === "all-bus"
                            ? "All BUs"
                            : "Business Unit"
                          : "Org"}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {activeContributionEntities.map((entity) => (
                          <button
                            key={entity.id}
                            type="button"
                            onClick={() =>
                              setSelectedContributionEntityId(entity.id)
                            }
                            className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition ${
                              activeContributionNode?.id === entity.id
                                ? "bg-orange-100 text-orange-800 border-orange-300"
                                : "bg-gray-50 text-gray-600 border-gray-300 hover:bg-orange-50"
                            }`}
                          >
                            {entity.name}
                            <span className="ml-2 font-bold">
                              {entity.totalConversations}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                {!activeContributionNode ? (
                  <div className="bg-orange-50 border border-orange-200 rounded p-3">
                    <p className="text-sm text-orange-700">
                      No collective contribution data is available for this time period.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Selected Entity Summary */}
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">
                          {activeContributionLevel === "contact"
                            ? "Contact Level"
                            : activeContributionLevel === "account"
                            ? "Account Level"
                            : activeContributionLevel === "bu"
                            ? "Business Unit Level"
                            : "Organization Level"}
                        </p>

                        <h3 className="text-base font-bold text-gray-800">
                          {activeContributionNode.name}
                        </h3>
                      </div>

                      <div className="text-right shrink-0">
                        <p className="text-2xl font-bold text-orange-700">
                          {activeContributionNode.totalConversations}
                        </p>

                        <p className="text-[10px] text-gray-500">
                          conversations
                        </p>
                      </div>
                    </div>

                    {/* Single Pane: Chart + Contribution Breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                      {/* Left: Momentum */}
                      <div className="bg-gray-50 rounded-lg border p-3">
                        <p className="text-sm font-bold text-gray-800 mb-1">
                          Conversation Momentum
                        </p>

                        <p className="text-[10px] text-gray-500 mb-3">
                          Daily conversations and cumulative conversation growth.
                        </p>

                        <div className="h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                              data={activeContributionNode.trendSeries || []}
                            >
                              <CartesianGrid
                                strokeDasharray="3 4"
                                stroke="#d1d5db"
                              />

                              <XAxis
                                dataKey="xValue"
                                tick={{ fontSize: 10, fill: "#9ca3af" }}
                                interval="preserveStartEnd"
                                tickFormatter={(xValue) =>
                                  activeContributionNode.trendSeries?.[xValue]
                                    ?.xLabel || ""
                                }
                              />

                              <YAxis
                                tick={{ fontSize: 10, fill: "#9ca3af" }}
                                allowDecimals={false}
                              />

                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) {
                                    return null;
                                  }

                                  const point = payload[0]?.payload;

                                  return (
                                    <div className="bg-white p-2 border rounded shadow text-xs">
                                      <p className="font-semibold">
                                        {moment(point.dateKey).format(
                                          "ddd, DD MMM YYYY"
                                        )}
                                      </p>

                                      <p>
                                        Daily conversations: {point.dailyCount}
                                      </p>

                                      <p>
                                        Cumulative conversations:{" "}
                                        {point.cumulativeCount}
                                      </p>
                                    </div>
                                  );
                                }}
                              />

                              <Legend />

                              <Bar
                                dataKey="dailyCount"
                                name="Daily Conversations"
                                fill="#84cc16"
                                radius={[3, 3, 0, 0]}
                              />

                              <Line
                                type="monotone"
                                dataKey="cumulativeCount"
                                name="Cumulative Conversations"
                                stroke="#EA580C"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Right: User Contribution */}
                      <div className="bg-gray-50 rounded-lg border p-3">
                        <p className="text-sm font-bold text-gray-800 mb-1">
                          Contribution by User
                        </p>

                        <p className="text-[10px] text-gray-500 mb-3">
                          Each percentage is the user&apos;s share of conversations
                          in this selected view.
                        </p>

                        {activeContributionNode.contributionSnapshot?.contributors
                          ?.length === 0 ? (
                          <p className="text-xs text-gray-500">
                            No user contribution data is available for this selection.
                          </p>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {activeContributionNode.contributionSnapshot.contributors.map(
                              (contributor, index) => {
                                const isMe =
                                  contributor.userId?.toString() ===
                                  userId?.toString();

                                return (
                                  <div
                                    key={`${activeContributionNode.id}-${contributor.userId}-${index}`}
                                    className={`bg-white rounded-lg p-3 shadow-sm border text-center ${
                                      isMe
                                        ? "border-lime-300 bg-lime-50"
                                        : ""
                                    }`}
                                  >
                                    <div className="h-28 w-28 mx-auto">
                                      <CircularProgressbarWithChildren
                                        value={contributor.percentOfTotal}
                                        circleRatio={0.75}
                                        styles={{
                                          path: {
                                            stroke: "#f97316",
                                            transform: "rotate(-135deg)",
                                            transformOrigin: "center center",
                                          },
                                          trail: {
                                            stroke: "#e5e7eb",
                                            transform: "rotate(-135deg)",
                                            transformOrigin: "center center",
                                          },
                                        }}
                                      >
                                        <div className="flex justify-center">
                                          <User
                                            username={contributor.username}
                                            defaultUserPic={userProfileIcon2}
                                          />
                                        </div>

                                        <p className="text-xs font-bold text-gray-700 mt-2">
                                          {contributor.percentOfTotal}%
                                        </p>
                                      </CircularProgressbarWithChildren>
                                    </div>

                                    <p className="text-[10px] font-semibold text-gray-700 mt-2 truncate">
                                      {contributor.userName?.split(" ")[0] || "User"}

                                      {isMe && (
                                        <span className="ml-1 text-[9px] text-orange-600 font-bold">
                                          (Me)
                                        </span>
                                      )}
                                    </p>

                                    <p className="text-[9px] text-gray-500 mt-1">
                                      {contributor.count} conversation
                                      {contributor.count === 1 ? "" : "s"}
                                    </p>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
        )}

        
        {/* 26 Jan 2026 - CONTRIBUTIONS VIEW */}
        {/* {viewMode !== "universe" && activeViewTab === "contributions" && activeCollectiveUserId === "all" &&
          conversationContributionSnapshot?.contributors?.length > 0 && (
            <div className="mt-6 animate-slide-in-left">
              <p className="text-lg font-bold m-1 p-1">
                Collective Contribution
              </p>
  
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {conversationContributionSnapshot.contributors.map((c, idx) => {
                  const isMe = c.userId === userId;
  
                  // console.log("username: ", c.username)
  
                  return (
                    <div
                      key={`${c.userId}-${idx}`}
                      className="bg-white rounded-lg p-3 shadow-sm border text-center"
                    >
                      <CircularProgressbarWithChildren
                        value={c.percentOfTotal}
                        circleRatio={0.75}
                        styles={{
                          path: {
                            stroke: "#f97316",
                            transform: "rotate(-135deg)",
                            transformOrigin: "center center",
                          },
                          trail: {
                            stroke: "#e5e7eb",
                            transform: "rotate(-135deg)",
                            transformOrigin: "center center",
                          },
                        }}
                      >
                       
  
                        <div className="flex justify-center">
                          <User
                            username={c.username}
                            defaultUserPic={userProfileIcon2}
                          />
                        </div>
  
                        
                        <p className="text-[10px] font-semibold mt-3">
                          {c.userName?.split(" ")[0]}
                          {isMe && (
                            <span className="ml-1 text-[9px] text-orange-600 font-bold">
                              (Me)
                            </span>
                          )}
                        </p>
  
                        
                        <p className="text-xs font-bold text-gray-700">
                          {c.percentOfTotal}%
                        </p>
  
                        
                        <p className="text-[10px] text-gray-500">
                          {c.count} conversations
                        </p>
                      </CircularProgressbarWithChildren>
                    </div>
                  );
                })}
              </div>
            </div>
        )} */}


        {/* ⭐ 26 Jan 2026 — UNIVERSE VIEW (Dedicated) */}

        {/* ================================
        🌍 Universe View (Executive Level)
        ================================ */}
        {viewMode === "universe" && (
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">

            {/* 30 Jan 2026 - EtAdmin View Tabs*/}
            {canViewAdminUniverse && (
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setUniverseTab("myOrg")}
                  className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    universeTab === "myOrg"
                      ? "bg-orange-600 text-white border-orange-700"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                  }`}
                >
                  My Org
                </button>

                <button
                  onClick={() => setUniverseTab("byOrg")}
                  className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                    universeTab === "byOrg"
                      ? "bg-orange-600 text-white border-orange-700"
                      : "bg-gray-100 text-gray-700 border-gray-300"
                  }`}
                >
                  By Org
                </button>
              </div>
            )}

            {/* 30 Jan 2026 - View Mode and myOrg */}
            {/* 20 Apr 2026 - Refined to include charts */}
            {viewMode === "universe" && universeTab === "myOrg" && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400">
                      Trinity Universe · <span className="font-bold text-orange-600">Conversations</span>
                    </p>
                    <h2 className="text-lg font-semibold text-gray-800">
                      Your Universe Performance — {universeTimeLabel}
                    </h2>
                  </div>
                </div>

                {/* Primary Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                    <p className="text-xs text-orange-700 uppercase tracking-wide">
                      My Org Contribution
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <p className="text-3xl font-bold text-orange-800">
                        {universeRankMeta?.myOrgTotal || 0}
                      </p>
                      <p className="text-sm text-orange-600">
                        conversations
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-orange-700">
                      {universeTotalCount > 0
                        ? `${Math.round(
                            ((universeRankMeta?.myOrgTotal || 0) / universeTotalCount) * 100
                          )}% of universe`
                        : "—"}
                    </p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <p className="text-xs text-purple-700 uppercase tracking-wide">
                      Universe Rank
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <p className="text-3xl font-bold text-purple-800">
                        #{universeRankMeta?.myRank ?? "—"}
                      </p>
                      <p className="text-sm text-purple-600">
                        of {universeRankMeta?.totalOrgs || 0}
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-purple-700">
                      Ranked by total conversations
                    </p>
                  </div>

                  <div className="bg-lime-50 rounded-lg p-4 border border-lime-100">
                    <p className="text-xs text-lime-700 uppercase tracking-wide">
                      Universe Activity
                    </p>

                    <div className="mt-2 flex items-end gap-2">
                      <p className="text-3xl font-bold text-lime-800">
                        {universeTotalCount}
                      </p>
                      <p className="text-sm text-lime-600">
                        conversations
                      </p>
                    </div>

                    <p className="mt-1 text-xs text-lime-700">
                      Across all organizations
                    </p>
                  </div>
                </div>

                {/* Narrative Insight */}
                {universeRankMeta && universeTotalCount > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 border">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Your organization contributed{" "}
                      <span className="font-bold">
                        {Math.round(
                          ((universeRankMeta?.myOrgTotal || 0) / universeTotalCount) * 100
                        )}%
                      </span>{" "}
                      of all conversations in the Trinity universe during this period,
                      placing you{" "}
                      <span className="font-bold">
                        #{universeRankMeta?.myRank}
                      </span>{" "}
                      out of{" "}
                      <span className="font-bold">
                        {universeRankMeta?.totalOrgs}
                      </span>{" "}
                      organizations.
                    </p>
                  </div>
                )}

                {/* 20 Apr 2026 - Expanded My Org universe chart view */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* LEFT SIDE — TRUE UNIVERSE VIEW */}
                  <div className="bg-white rounded-lg border p-4 space-y-5">
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        True Universe View
                      </p>
                      <p className="text-xs text-gray-500">
                        How the full universe is trending and how each org is contributing to that movement.
                      </p>
                    </div>

                    {/* Top Left - Full universe trend */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Universe Activity Trend
                      </p>

                      <div className="h-64">
                        <ResponsiveContainer>
                          <LineChart
                            data={universeDailyCumulativeData}
                            margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="xValue"
                              tick={{ fontSize: 10 }}
                              tickFormatter={(x) =>
                                universeDailyCumulativeData?.[x]?.xLabel || ""
                              }
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;

                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Daily: {p.dailyCount}</p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Legend />
                            <Bar
                              dataKey="dailyCount"
                              fill="#84cc16"
                              name="Daily Universe"
                              barSize={18}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulativeCount"
                              stroke="#EA580C"
                              strokeWidth={2}
                              dot={{ r: 2 }}
                              name="Cumulative Universe"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bottom Left - each org contribution trend */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-2">
                        Org Contribution Trend
                      </p>

                      <div className="h-72">
                        <ResponsiveContainer>
                          <LineChart margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              type="number"
                              dataKey="xValue"
                              tick={{ fontSize: 10 }}
                              tickFormatter={(x) =>
                                universeOrgCompareSeries?.[0]?.series?.[x]?.xLabel || ""
                              }
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;

                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Legend />
                            {universeOrgCompareSeries.map((orgSeries, idx) => (
                              <Line
                                key={orgSeries.orgId}
                                data={orgSeries.series}
                                type="monotone"
                                dataKey="cumulativeCount"
                                stroke={orgSeries.isMyOrg ? "#EA580C" : getUserColor(orgSeries.orgId, idx + 1)}
                                strokeWidth={orgSeries.isMyOrg ? 3 : 1.75}
                                dot={false}
                                name={orgSeries.label}
                                connectNulls
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE — MY ORG CONTRIBUTORS */}
                  <div className="bg-white rounded-lg border p-4 space-y-5">
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        My Org Contributors
                      </p>
                      <p className="text-xs text-gray-500">
                        Your organization’s momentum and the ranked contribution trend of your people.
                      </p>
                    </div>

                    {/* Top Right - My Org combined trend */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-700">
                          My Org Trend
                        </p>
                        <p className="text-[10px] text-gray-500">
                          Total: <span className="font-bold">{myOrgUniverseData?.total || 0}</span>
                        </p>
                      </div>

                      <div className="h-64 mt-2">
                        <ResponsiveContainer>
                          <LineChart data={myOrgUniverseSeries}>
                            <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                            <XAxis
                              dataKey="xValue"
                              tick={{ fontSize: 10, fill: "#9ca3af" }}
                              interval="preserveStartEnd"
                              tickFormatter={(x) =>
                                myOrgUniverseSeries?.[x]?.xLabel || ""
                              }
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;

                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulativeCount"
                              stroke="#EA580C"
                              strokeWidth={2}
                              dot={{
                                r: 2,
                                fill: "#EA580C",
                                stroke: "#fff",
                                strokeWidth: 1,
                              }}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bottom Right - ranked contributors + selected user mini chart */}
                    <div className="rounded-lg border bg-gray-50 p-3">
                      <p className="text-xs font-semibold text-gray-700 mb-3">
                        Contributor Momentum
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        {/* LEFT — Ranked Users */}
                          <div className="space-y-1 md:col-span-1">
                            {!myOrgUniverseData?.users ||
                            Object.keys(myOrgUniverseData.users).length === 0 ? (
                              <p className="text-xs text-gray-400 p-2">
                                No contributor data available for this period.
                              </p>
                            ) : (
                              Object.entries(myOrgUniverseData.users)
                                .sort((a, b) => b[1] - a[1])
                                .map(([uid, count], uIdx, arr) => {
                                  const fullName = userIdToFirstNameMap[uid] || "User";

                                  return (
                                    <button
                                      key={uid}
                                      onClick={() => handleUserSelect(userOrgId?.toString(), uid)}
                                      className={`w-full flex justify-between items-center text-xs p-2 border-b rounded
                                        ${
                                          // getSelectedUserForOrg(userOrgId?.toString(), arr) === uid
                                          selectedMyOrgUserId === uid
                                            ? "bg-orange-100 text-orange-800 font-semibold"
                                            : "hover:bg-gray-50 text-gray-600"
                                        }`}
                                    >
                                      <span className="flex items-center gap-2">
                                        <span className="text-[10px] text-gray-400">
                                          {uIdx + 1}
                                        </span>

                                        <span>
                                          {fullName}
                                        </span>
                                      </span>

                                      <span className="font-bold">{count}</span>
                                    </button>
                                  );
                                })
                            )}
                          </div>

                          {/* RIGHT — Selected User Mini Chart */}
                          <div className="h-32 md:col-span-2">
                            <ResponsiveContainer>
                              <LineChart
                                data={
                                  universeUserCumulativeSeries?.[userOrgId?.toString()]?.[selectedMyOrgUserId] || []
                                }
                                // data={
                                //   universeUserCumulativeSeries?.[userOrgId?.toString()]?.[
                                //     getSelectedUserForOrg(
                                //       userOrgId?.toString(),
                                //       Object.entries(myOrgUniverseData?.users || {}).sort((a, b) => b[1] - a[1])
                                //     )
                                //   ] || []
                                // }
                              >
                                <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                                <XAxis hide />
                                <YAxis hide />
                                <Tooltip
                                  content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const p = payload[0].payload;

                                    return (
                                      <div className="bg-white p-2 border rounded shadow text-xs">
                                        <p className="font-semibold">
                                          {moment(p.dateKey).format("DD MMM YYYY")}
                                        </p>
                                        <p>Cumulative: {p.cumulativeCount}</p>
                                      </div>
                                    );
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="cumulativeCount"
                                  stroke="#EA580C"
                                  strokeWidth={2}
                                  dot={{ r: 2 }}
                                  activeDot={{ r: 4 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>

                            <p className="mt-1 text-[10px] text-gray-400 text-center">
                              {(() => {
                                const selectedUid = getSelectedUserForOrg(
                                  userOrgId?.toString(),
                                  Object.entries(myOrgUniverseData?.users || {}).sort((a, b) => b[1] - a[1])
                                );

                                const fullName = userIdToFirstNameMap[selectedUid] || "User";

                                return (
                                  <>
                                    {fullName} · conversation momentum
                                  </>
                                );
                              })()}
                            </p>
                          </div>

                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* 30 Jan 2026 - View Mode and byOrg  */}
            {viewMode === "universe" && universeTab === "byOrg" && canViewAdminUniverse && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(universeByOrgMap)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([orgId, data], idx) => (
                    <div
                      key={orgId}
                      className="bg-white rounded-lg p-4 shadow-sm border"
                    >
                      {/* <p className="text-xs text-gray-400 uppercase">
                        Organization #{idx + 1}
                      </p> */}
                      {/* <p
                        className={`inline-block text-xs uppercase font-semibold px-3 py-1 rounded-full ${rankBgClass(idx)}`}
                      >
                        Organization # {idx + 1}
                      </p> */}
  
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-400 uppercase">
                          Organization
                        </p>
  
                        <span
                          className={`text-xs font-bold px-3 py-0.5 rounded-full ${rankBgClass(idx)}`}
                        >
                          # <span className="text-lg">{idx + 1}</span>
                        </span>
                      </div>
  
                      {/* 05 Feb 2026 - Org Name Visibility Toggle */}
                                            
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-lg font-bold text-gray-800">
                          {showOrgNameMap[orgId]
                            ? orgIdToNameMap[orgId] || "Unknown Organization"
                            : getCrypticOrgName(orgIdToNameMap[orgId])}
                        </p>
  
                        <button
                          onClick={() => toggleOrgNameVisibility(orgId)}
                          className="p-1 rounded hover:bg-gray-100"
                          title={showOrgNameMap[orgId] ? "Hide org name" : "Show org name"}
                        >
                          {showOrgNameMap[orgId] ? (
                            <EyeSlashIcon className="h-4 w-4 text-gray-500" />
                          ) : (
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
  
                      {/* <p className="text-lg font-bold text-gray-800">
                        {/* Org ID: {orgId} }
                        {orgIdToNameMap[orgId] || "Unknown Organization"}
                      </p> */}
  
                      <p className="text-sm text-gray-600 mt-1">
                        Total Conversations:{" "}
                        <span className="font-bold">{data.total}</span>
                      </p>
  
                      {/* 📈 31 Jan 2026 - Growth Chart */}
                      <div className="mt-4 h-28">
                        <ResponsiveContainer>
                          <LineChart data={universeOrgCumulativeSeries[orgId] || []}>
                            {/* <CartesianGrid strokeDasharray="3 3" /> */}
                            <CartesianGrid
                              strokeDasharray="3 4"
                                stroke="#d1d5db"
                              // strokeDasharray="2 4"
                              // stroke="#e5e7eb"
                            />
  
                            <XAxis
                              dataKey="xValue"
                              tick={{ fontSize: 9, fill: "#9ca3af" }}
                              interval="preserveStartEnd"
                              tickFormatter={(x) =>
                                universeOrgCumulativeSeries[orgId]?.[x]?.xLabel || ""
                              }
                              // tickFormatter={(x) =>
                              //   universeOrgCumulativeSeries[orgId]?.[x]?.xLabel || ""
                              // }
                              // hide
                            />
                            <YAxis hide />
                            <Tooltip
                              content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0]?.payload;
  
                                return (
                                  <div className="bg-white p-2 border rounded shadow text-xs">
                                    <p className="font-semibold">
                                      {moment(p.dateKey).format("DD MMM YYYY")}
                                    </p>
                                    <p>Cumulative: {p.cumulativeCount}</p>
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="cumulativeCount"
                              stroke="#EA580C"
                              strokeWidth={2}
                              // dot={false}
                              dot={{
                                r: 2,
                                fill: "#EA580C",
                                stroke: "#fff",
                                // stroke: "#84cc16",
                                strokeWidth: 1,
                              }}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
  
                      {/* 👥 Contributors + 📈 User Focus */}
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
  
                        {/* LEFT — Ranked Users */}
                        {/* 05 Feb 2026 - Masked Data */}
                        <div className="space-y-1 md:col-span-1">
                          {Object.entries(data.users)
                            .sort((a, b) => b[1] - a[1])
                            .map(([uid, count], uIdx, arr) => {
                              const fullName = userIdToFirstNameMap[uid] || "User";
                              // const showFull = showUserNameMap[uid];

                              return (
                                <button
                                  key={uid}
                                  onClick={() => handleUserSelect(orgId, uid)}
                                  className={`w-full flex justify-between items-center text-xs p-2 border-b rounded
                                    ${
                                      getSelectedUserForOrg(orgId, arr) === uid
                                        ? "bg-orange-100 text-orange-800 font-semibold"
                                        : "hover:bg-gray-50 text-gray-600"
                                    }`}
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-400">
                                      {uIdx + 1}
                                    </span>

                                    <span>
                                      {/* {showFull ? fullName : getCrypticUserName(fullName)} */}
                                      {showOrgNameMap[orgId]
                                      ? fullName
                                      : getCrypticUserName(fullName)}
                                    </span>
                                  </span>

                                  <span className="font-bold">{count}</span>
                                </button>
                              );
                            })}
                        </div>


                        {/* <div className="space-y-1 md:col-span-1">
                          {Object.entries(data.users)
                            .sort((a, b) => b[1] - a[1])
                            .map(([uid, count], uIdx, arr) => (
                              <button
                                key={uid}
                                onClick={() => handleUserSelect(orgId, uid)}
                                className={`w-full flex justify-between items-center text-xs p-2 border-b rounded
                                  ${
                                    getSelectedUserForOrg(orgId, arr) === uid
                                      ? "bg-orange-100 text-orange-800 font-semibold"
                                      : "hover:bg-gray-50 text-gray-600"
                                  }`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="text-[10px] text-gray-400">
                                    {uIdx + 1}
                                  </span>
                                  <span>{userIdToFirstNameMap[uid] || "User"}</span>
                                </span>
  
                                <span className="font-bold">{count}</span>
                              </button>
                            ))}
                        </div> */}
  
                        {/* RIGHT — Selected User Mini Chart */}
                        <div className="h-32 md:col-span-2">
                          <ResponsiveContainer>
                            <LineChart
                              data={
                                universeUserCumulativeSeries?.[orgId]?.[
                                  getSelectedUserForOrg(
                                    orgId,
                                    Object.entries(data.users).sort((a, b) => b[1] - a[1])
                                  )
                                ] || []
                              }
                            >
                              <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                              <XAxis hide />
                              <YAxis hide />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const p = payload[0].payload;
  
                                  return (
                                    <div className="bg-white p-2 border rounded shadow text-xs">
                                      <p className="font-semibold">
                                        {moment(p.dateKey).format("DD MMM YYYY")}
                                      </p>
                                      <p>Cumulative: {p.cumulativeCount}</p>
                                    </div>
                                  );
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="cumulativeCount"
                                stroke="#EA580C"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                                activeDot={{ r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>

                          {/* 05 Feb 2026 */}
                          <p className="mt-1 text-[10px] text-gray-400 text-center">
                              {(() => {
                                const selectedUid = getSelectedUserForOrg(
                                  orgId,
                                  Object.entries(data.users).sort((a, b) => b[1] - a[1])
                                );

                                const fullName = userIdToFirstNameMap[selectedUid] || "User";

                                return (
                                  <>
                                    {/* {showUserNameMap[selectedUid]
                                      ? fullName
                                      : getCrypticUserName(fullName)}{" "}
                                    · reflection momentum */}
                                    {showOrgNameMap[orgId]
                                      ? fullName
                                      : getCrypticUserName(fullName)} · conversation momentum
                                  </>
                                );
                              })()}
                            </p>
  
                          {/* <p className="mt-1 text-[10px] text-gray-400 text-center">
                            {(userIdToFirstNameMap[
                              getSelectedUserForOrg(
                                orgId,
                                Object.entries(data.users).sort((a, b) => b[1] - a[1])
                              )
                            ] || "User")} · conversation momentum
                          </p> */}
                        </div>
  
                      </div>
                    </div>
                  ))}
              </div>
            )}

          </div>
        )}


        {/* 23 Jan 2026 */}
        {showCreateModal && (
        <EtModalCreateUserConversationLanding
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          userId={userId}
          userOrgId={userOrgId}
          refetchUserConversations={refetchUserConversationMaster}
        /> 
        )}

        {showUpdateModal && selectedConversation && (
          <EtModalUpdateUserConversationLanding
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedConversation(null);
            }}
            userId={userId}
            userOrgId={userOrgId}
            allUsers={allUsers}           // 06 Feb 2026
            conversationMaster={selectedConversation}
            weekStarting={
              selectedConversation
                ?.sourceConversation
                ?.userConversationId
                ?.weekStarting
            }
            refetchUserConversations={refetchUserConversationMaster}
          />
        )}
        {showDeleteModal && (
        <EtModalDeleteUserConversationLanding
          showModal={showDeleteModal}
          conversationMaster={selectedDeleteConversation}
          refetchUserConversations={refetchUserConversationMaster}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedDeleteConversation(null);
          }}
        />
      )}
    </div>
  );
};

export default EtTrinityConversationCenter;